<template>
  <div class="panorama-page p-2 p-md-3" :class="{ 'dark-mode': isDarkMode }">
    <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap">
      <div class="d-flex align-items-center flex-wrap mb-2 mb-md-0 panorama-toolbar">
        <h3 class="text-title mb-0 font-weight-bold tracking-wide mr-3">
          <b-icon icon="display" class="mr-2 text-warning"></b-icon>策略全景解析
        </h3>
        <div class="d-flex align-items-center panorama-pill px-2 py-1 rounded-pill border">
          <b-icon icon="calendar3" class="text-warning mx-2" font-scale="0.9"></b-icon>
          <b-form-input 
            type="date" 
            v-model="selectedDate" 
            size="sm" 
            class="custom-datepicker border-0 bg-transparent shadow-none px-1" 
            style="width: 135px; font-size: 0.875rem;"
            @change="initializePage"
          ></b-form-input>
          <b-button 
            variant="link" 
            size="sm" 
            class="text-warning p-0 mx-2 text-decoration-none xx-small" 
            @click="resetToToday"
            v-if="selectedDate !== todayStr"
          >
            今天
          </b-button>
        </div>
        <span
          class="panorama-pill px-3 py-1 rounded-pill border ml-2 text-nowrap"
          role="status"
          title="V2主升资金组：2026-01-05为A组，按交易日交替；V3使用共享资金池"
        >{{ v2AbGroupLabel }}</span>
        <div class="d-flex align-items-center panorama-pill px-3 py-1 rounded-pill border ml-2">
          <b-form-checkbox
            v-model="supplementH67"
            @change="updateMonitorConfig()"
            switch
            class="panorama-switch xx-small"
          >
            <span style="font-size: 0.825rem; font-weight: 500; cursor: pointer;">补充H6/7</span>
          </b-form-checkbox>
          <b-form-checkbox
            v-model="filterTopicNet5"
            @change="updateMonitorConfig()"
            switch
            class="panorama-switch xx-small ml-3"
          >
            <span style="font-size: 0.825rem; font-weight: 500; cursor: pointer;">NET5≥0</span>
          </b-form-checkbox>
        </div>
        <b-button
          variant="outline-info"
          size="sm"
          class="rounded-pill px-3 ml-2"
          :disabled="refreshing"
          @click="refreshPage"
        >
          <b-spinner v-if="refreshing" small class="mr-1"></b-spinner>
          <b-icon v-else icon="arrow-clockwise" class="mr-1"></b-icon>刷新
        </b-button>

        <!-- 实盘自动同步开关（仅当日有效） -->
        <div
          class="d-flex align-items-center panorama-pill px-2 py-1 rounded-pill border ml-2 transition-all"
          :class="autoSync && isCurrentDateToday ? 'border-success-50' : ''"
          :title="!isCurrentDateToday ? '仅当日支持实盘自动同步' : (autoSync ? '已开启实盘自动同步（30秒/1分钟收到广播静默刷新）' : '已关闭自动同步，点击可开启')"
        >
          <b-form-checkbox
            v-model="autoSync"
            switch
            class="panorama-switch xx-small"
            :disabled="!isCurrentDateToday"
            @change="toggleAutoSync"
          >
            <span
              :class="autoSync && isCurrentDateToday ? 'text-success font-weight-bold' : 'text-muted'"
              style="font-size: 0.8rem; cursor: pointer; user-select: none;"
            >
              <span v-if="autoSync && isCurrentDateToday" class="pulse-dot mr-1">●</span>
              {{ autoSync && isCurrentDateToday ? '自动同步' : '手动模式' }}
            </span>
          </b-form-checkbox>
          <span
            v-if="lastSyncTime && autoSync && isCurrentDateToday"
            class="text-muted ml-1 font-monospace"
            style="font-size: 0.72rem; opacity: 0.85;"
            :title="`最后静默同步时间: ${lastSyncTime}`"
          >
            {{ lastSyncTime }}
          </span>
        </div>
      </div>
      <b-button variant="outline-secondary" class="rounded-pill px-4 shadow-sm" @click="$router.back()">
        <b-icon icon="arrow-left" class="mr-1"></b-icon> 返回
      </b-button>
    </div>

    <div class="row mb-2 compact-control-panel">
      <div class="col-12 col-xl-7 mb-2 mb-xl-0">
        <b-card class="panorama-card shadow-sm rounded-lg" body-class="p-2">
          <div class="d-flex justify-content-between align-items-center flex-wrap">
            <b-button
              variant="link"
              size="sm"
              class="text-title font-weight-bold text-decoration-none p-0"
              :aria-expanded="positionConfigExpanded ? 'true' : 'false'"
              aria-controls="panorama-position-config"
              @click="positionConfigExpanded = !positionConfigExpanded"
            >
              <b-icon :icon="positionConfigExpanded ? 'chevron-down' : 'chevron-right'" class="mr-1"></b-icon>
              仓位配置<span class="text-muted xx-small font-weight-normal ml-2">{{ positionConfigExpanded ? '收起' : '展开' }}</span>
            </b-button>
            <b-button v-if="positionConfigExpanded" size="sm" variant="warning" class="rounded-pill px-3" :disabled="savingConfig" @click="savePositionConfig">
              <b-spinner v-if="savingConfig" small class="mr-1"></b-spinner>保存仓位
            </b-button>
          </div>
          <b-collapse id="panorama-position-config" v-model="positionConfigExpanded">
            <div class="text-muted xx-small mt-2 mb-1">模拟盘与实盘独立控制（配置0%表示不开仓），保存后下一周期生效</div>

          <!-- V2 模拟盘 -->
          <div class="position-config-row mb-1">
            <div class="strategy-label text-warning">V2模拟</div>
            <div v-for="(value, index) in v2PositionPcts" :key="`v2-sim-${index}`" class="position-input-wrap">
              <label>第{{ index + 1 }}笔<span v-if="Number(v2PositionPcts[index]) === 0" class="text-danger ml-1">不开仓</span></label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="v2PositionPcts[index]" type="number" min="0" max="100" step="0.5" placeholder="0"></b-form-input>
              </b-input-group>
            </div>
            <div class="position-total">
              模拟总仓位 <strong>{{ positionTotal(v2PositionPcts) }}%</strong>
              <span class="today-buy-targets ml-2">
                ｜ 当日买入：
                <template v-if="todayBuys.v2 && todayBuys.v2.length">
                  <span v-for="(item, idx) in todayBuys.v2" :key="`v2-target-${item.code || idx}`" class="today-buy-target-item">
                    <strong class="target-name">{{ item.name }}</strong>
                    <b-button
                      variant="link"
                      size="sm"
                      class="fire-buy-btn p-0"
                      title="点击点火触发实盘买入"
                      @click.stop="openRealBuyModal(item, 'v2', idx)"
                    >
                      🔥
                    </b-button>
                    <span v-if="idx < todayBuys.v2.length - 1" class="text-secondary mr-1">、</span>
                  </span>
                </template>
                <strong v-else class="text-muted">暂无</strong>
              </span>
            </div>
          </div>

          <!-- V2 实盘 -->
          <div class="position-config-row mb-2 border-bottom panorama-divider pb-1">
            <div class="strategy-label text-danger">V2实盘</div>
            <div v-for="(value, index) in v2RealPositionPcts" :key="`v2-real-${index}`" class="position-input-wrap">
              <label>第{{ index + 1 }}笔<span v-if="Number(v2RealPositionPcts[index]) === 0" class="text-danger ml-1">不开仓</span></label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="v2RealPositionPcts[index]" type="number" min="0" max="100" step="0.5" placeholder="0"></b-form-input>
              </b-input-group>
            </div>
            <div class="position-total">
              实盘总仓位 <strong>{{ positionTotal(v2RealPositionPcts) }}%</strong>
            </div>
          </div>

          <div v-for="kind in ['sim']" :key="`rebound-${kind}`" class="position-config-row mb-1">
            <div class="strategy-label text-info">反弹{{ kind === 'sim' ? '模拟' : '实盘' }}</div>
            <div v-for="(value, index) in (kind === 'sim' ? reboundPositionPcts : reboundRealPositionPcts)" :key="index" class="position-input-wrap">
              <label>第{{ index + 1 }}笔</label>
              <b-input-group size="sm" append="%">
                <b-form-input v-if="kind === 'sim'" v-model.number="reboundPositionPcts[index]" type="number" min="0" max="100" step="0.5"></b-form-input>
                <b-form-input v-else v-model.number="reboundRealPositionPcts[index]" type="number" min="0" max="100" step="0.5"></b-form-input>
              </b-input-group>
            </div>
            <div class="position-total">总仓位 <strong>{{ positionTotal(kind === 'sim' ? reboundPositionPcts : reboundRealPositionPcts) }}%</strong><span v-if="kind === 'sim'" class="ml-2">当日买入：{{ todayBuyText('rebound') }}</span></div>
          </div>
          <div v-for="group in ['v2', 'v3']" :key="`rebound-real-${group}`" class="position-config-row mb-1">
            <div class="strategy-label text-info">反弹→{{ group.toUpperCase() }}组</div>
            <b-form-checkbox v-model="reboundRealGroups[group].enabled" switch size="sm" class="mr-2" :disabled="savingConfig || !isCurrentDateToday" @change="onReboundRealGroupToggle($event, group)">实盘买入</b-form-checkbox>
            <div v-for="(value, index) in reboundRealGroups[group].pcts" :key="index" class="position-input-wrap">
              <label>第{{ index + 1 }}笔</label>
              <b-input-group size="sm" append="%"><b-form-input v-model.number="reboundRealGroups[group].pcts[index]" type="number" min="0" max="100" step="0.5"></b-form-input></b-input-group>
            </div>
            <div class="position-total">总仓位 <strong>{{ positionTotal(reboundRealGroups[group].pcts) }}%</strong><span v-if="!reboundRealGroups[group].enabled" class="text-muted ml-2">买入关闭</span></div>
          </div>
          <div class="text-muted xx-small mb-2">反弹两组独立开关；不自动改变主升、V3的买入配置。关闭仅停止新买入，已有实盘持仓继续退出。</div>
          <div class="d-flex align-items-center flex-wrap xx-small my-2">
            <strong class="mr-2">V2实盘对接</strong>
            <b-button-group size="sm">
              <b-button :variant="v2MarketMode === 'main' ? 'warning' : 'outline-secondary'" :disabled="savingConfig || !isCurrentDateToday" @click="changeV2MarketMode('main')">主升</b-button>
              <b-button :variant="v2MarketMode === 'rebound' ? 'info' : 'outline-secondary'" :disabled="savingConfig || !isCurrentDateToday" @click="changeV2MarketMode('rebound')">反弹</b-button>
            </b-button-group>
            <span class="text-muted ml-2">人工切换；建议只提示。旧持仓继续原策略退出。</span>
          </div>
          <!-- V3 模拟盘 -->
          <div class="position-config-row mb-1">
            <div class="strategy-label text-info">V3模拟</div>
            <div v-for="(value, index) in v3PositionPcts" :key="`v3-sim-${index}`" class="position-input-wrap">
              <label>第{{ index + 1 }}笔<span v-if="Number(v3PositionPcts[index]) === 0" class="text-danger ml-1">不开仓</span></label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="v3PositionPcts[index]" type="number" min="0" max="100" step="0.5" placeholder="0"></b-form-input>
              </b-input-group>
            </div>
            <div class="position-total">
              模拟总仓位 <strong>{{ positionTotal(v3PositionPcts) }}%</strong>
              <span class="today-buy-targets ml-2">
                ｜ 当日买入：
                <template v-if="todayBuys.v3 && todayBuys.v3.length">
                  <span v-for="(item, idx) in todayBuys.v3" :key="`v3-target-${item.code || idx}`" class="today-buy-target-item">
                    <strong class="target-name">{{ item.name }}</strong>
                    <b-button
                      variant="link"
                      size="sm"
                      class="fire-buy-btn p-0"
                      title="点击点火触发实盘买入"
                      @click.stop="openRealBuyModal(item, 'v3', idx)"
                    >
                      🔥
                    </b-button>
                    <span v-if="idx < todayBuys.v3.length - 1" class="text-secondary mr-1">、</span>
                  </span>
                </template>
                <strong v-else class="text-muted">暂无</strong>
              </span>
            </div>
          </div>

          <!-- V3 实盘 -->
          <div class="position-config-row">
            <div class="strategy-label text-success">V3实盘</div>
            <div v-for="(value, index) in v3RealPositionPcts" :key="`v3-real-${index}`" class="position-input-wrap">
              <label>第{{ index + 1 }}笔<span v-if="Number(v3RealPositionPcts[index]) === 0" class="text-danger ml-1">不开仓</span></label>
              <b-input-group size="sm" append="%">
                <b-form-input v-model.number="v3RealPositionPcts[index]" type="number" min="0" max="100" step="0.5" placeholder="0"></b-form-input>
              </b-input-group>
            </div>
            <div class="position-total">
              实盘总仓位 <strong>{{ positionTotal(v3RealPositionPcts) }}%</strong>
            </div>
          </div>
          </b-collapse>
        </b-card>
      </div>
      <div class="col-12 col-xl-5">
        <b-card class="crowding-card panorama-card shadow-sm rounded-lg" :class="`crowding-${crowding.level || 'unknown'}`" body-class="p-2">
          <div class="risk-compact-header">
            <div class="d-flex align-items-center flex-wrap">
              <strong class="text-title mr-2">题材拥挤度</strong>
              <strong v-if="crowding.available" class="risk-compact-ratio mr-2">{{ (crowding.positive_ratio * 100).toFixed(1) }}%</strong>
              <b-badge :variant="crowdingBadgeVariant">{{ crowdingLevelText }}</b-badge>
            </div>
            <span v-if="crowding.date" class="xx-small text-muted">{{ crowding.date }}<span v-if="crowding.is_stale" class="text-warning ml-1">非当日</span></span>
          </div>
          <div class="risk-compact-advice" :class="{ 'text-danger': crowding.level === 'blocked' }">{{ crowding.message || '拥挤度数据不可用' }}</div>
          <div v-if="crowding.available" class="xx-small text-muted mb-1">
            正预期 {{ crowding.positive_count }}/{{ crowding.valid_count }} · 覆盖 {{ (crowding.coverage_ratio * 100).toFixed(1) }}%（{{ crowding.valid_count }}/{{ crowding.topic_count }}）
          </div>
          <div v-if="crowding.market_style" class="risk-compact-style">
            <div class="risk-compact-header">
              <div class="d-flex align-items-center flex-wrap">
                <strong class="xx-small text-title mr-2">风格决策</strong>
                <b-badge :variant="activeModeBadgeVariant" class="mr-1">{{ effectiveActiveModeText }}</b-badge>
                <b-badge :variant="overallGateVariant" :title="overallGateStatusTooltip">{{ overallGateStatusText }}</b-badge>
              </div>
              <span v-if="crowding.market_style.prev_trade_date" class="xx-small text-muted">基准 {{ crowding.market_style.prev_trade_date }}</span>
            </div>
            <div class="risk-compact-indicators xx-small">
              <span>前日成交 <strong>{{ (crowding.market_style.prev_turnover_bi / 10000).toFixed(2) }}万亿</strong></span>
              <span>5日均 <strong>{{ (crowding.market_style.prev_ma5_bi / 10000).toFixed(2) }}万亿</strong></span>
              <span>Timing <strong :class="crowding.market_style.timing_gate_action === 'block' ? 'text-danger' : ''">{{ crowding.market_style.timing_name }}</strong></span>
              <span>高潮防守 <strong>{{ crowding.market_style.climax_defense_status }}</strong><b-icon icon="question-circle" class="ml-1" :title="climaxDefenseHelpText"></b-icon></span>
            </div>
            <div v-if="weakTimingLowVolumeEnabled" class="risk-compact-gate xx-small" :class="{ 'is-blocked': weakTimingLowVolumeBlocked }">
              <strong>弱势低量择时</strong>
              <b-badge :variant="weakTimingLowVolumeVariant">{{ weakTimingLowVolumeStatusText }}</b-badge>
              <span>Timing {{ weakTimingValueText }} · 成交 {{ weakTimingTurnoverText }}</span>
              <strong class="ml-auto">V2新开仓：{{ weakTimingLowVolumeActionText }}</strong>
            </div>
          </div>
          <details class="risk-compact-details xx-small">
            <summary>阈值、仓位建议与决策说明</summary>
            <div class="text-muted mt-2">拥挤度：&lt;78% 正常 · 78%–&lt;82% 半仓 · ≥82% 禁止新开仓；覆盖率至少80%才启用判断。</div>
            <div v-if="crowding.recommendations" class="text-muted mt-1">V2 {{ recommendedPositionText('v2') }}；V3 {{ recommendedPositionText('v3') }}</div>
            <template v-if="crowding.market_style">
              <div v-if="crowding.market_style && crowding.market_style.switch_suggested" class="text-info mt-1">风格建议：{{ crowding.market_style.suggested_mode === 'rebound' ? 'V2反弹' : 'V2主升' }}，请人工决定是否切换。</div>
              <div class="text-muted mt-1">主升与反弹各自持续模拟；外部实盘由人工选择，系统不自动切换。</div>
              <div v-if="crowding.market_style.days_above_20t !== undefined" class="text-muted mt-1">近3日 {{ crowding.market_style.days_above_20t }}/3天 ≥ 2.0万亿。</div>
              <div v-if="weakTimingLowVolumeEnabled" class="text-muted mt-1">弱势低量边界：Timing ≤ {{ weakTimingMax }} 且成交 &lt; {{ weakTimingTurnoverThresholdText }}；存量卖出与风控照常。{{ crowding.market_style.weak_low_volume_gate_detail }}</div>
              <div class="market-style-summary px-2 py-1 rounded mt-2" :class="marketStyleSummaryClass">
                {{ marketStyleSummaryParts.before }}<span>{{ marketStyleSummaryParts.climax }}</span><b-icon v-if="marketStyleSummaryParts.climax" icon="question-circle" class="mx-1" :title="summaryClimaxStatusHelpText"></b-icon>{{ marketStyleSummaryParts.after }}
              </div>
            </template>
          </details>
        </b-card>
      </div>
    </div>

    <section class="candidate-tabs-section mt-3" aria-label="策略备选题材与备选股">
      <b-tabs v-model="candidateTab" class="candidate-tabs">
        <b-tab :title="topics.length > 0 && checkedTopicsCount < topics.length ? `V2主升备选 (${checkedTopicsCount}/${topics.length})` : `V2主升备选 (${topics.length})`">
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="warning" label="Loading..."></b-spinner>
      <div class="text-muted-gold mt-3">正在获取 {{ selectedDate }} 监控数据...</div>
    </div>

    <div v-else-if="topics.length === 0" class="text-center py-5">
      <b-icon icon="inbox" font-scale="3" class="text-muted mb-3"></b-icon>
      <div class="text-muted">{{ selectedDate }} 无监控题材或暂未获取到数据</div>
    </div>

    <div v-else class="row no-gutters custom-row-5">
      <div v-for="(topic, index) in topics" :key="index" class="col-12 col-md-4 col-xl-20 mb-2 px-1">
        <b-card class="panorama-card shadow-sm h-100 rounded-lg overflow-hidden" :class="{ 'card-unchecked-excluded': !isTopicChecked(topic) }" body-class="p-1 p-md-2">
          <!-- 头部 -->
          <div class="d-flex justify-content-between align-items-start mb-2 pb-2 border-bottom panorama-divider">
            <div class="topic-header-main flex-grow-1 mr-2" style="min-width: 0;">
              <div class="d-flex align-items-center flex-nowrap mb-1">
                <b-form-checkbox
                  switch
                  size="sm"
                  :checked="isTopicChecked(topic)"
                  @change="handleTopicCheckToggle(topic, $event)"
                  class="mr-2 custom-switch-topic flex-shrink-0"
                  :title="isTopicChecked(topic) ? '备选题材：已开启（点击关闭排除12小时）' : `已排除12小时（${getTopicRemainingHoursText(topic)}后自动恢复，点击可重新开启）`"
                  @click.native.stop
                ></b-form-checkbox>
                <h6 class="mb-0 topic-name-title font-weight-bold cursor-pointer hover-primary d-inline-flex align-items-center flex-wrap text-truncate" :title="topic.topic_name" @click="showTopicKline(topic)">
                  <span class="topic-sequence mr-1">{{ index + 1 }}.</span><span>{{ topic.topic_name }}</span>
                  <span v-if="topic.h_marker || topic.d20_marker" class="text-muted-gold font-weight-normal xx-small ml-1" style="font-size: 0.75rem;">
                    [{{ [topic.h_marker, topic.d20_marker].filter(Boolean).join(',') }}]
                  </span>
                  <b-badge
                    v-if="!isTopicChecked(topic)"
                    variant="danger"
                    class="xx-small ml-1 font-weight-normal badge-topic-excluded d-inline-flex align-items-center"
                    :title="`已排除备选（剩余 ${getTopicRemainingHoursText(topic)}）`"
                  >
                    已排除 {{ getTopicRemainingHoursText(topic) }}
                  </b-badge>
                  <b-badge
                    v-else
                    variant="soft-success"
                    class="xx-small ml-1 font-weight-normal badge-topic-included d-inline-flex align-items-center"
                    title="当日备选题材（已开启）"
                  >
                    备选
                  </b-badge>
                </h6>
              </div>
              <div class="d-flex align-items-center flex-wrap mb-1">
                <b-badge variant="soft-warning" class="xx-small mr-1 d-inline-block">共 {{ topic.total_candidates }} 备选</b-badge>
                <b-badge 
                  v-if="formatTopicNet5(topic)" 
                  :class="getNet5BadgeClass(topic)" 
                  class="xx-small font-weight-bold d-inline-block"
                  :title="`近5日净涨停: ${formatTopicNet5(topic)}`"
                >
                  NET5 {{ formatTopicNet5(topic) }}
                </b-badge>
                <b-badge 
                  v-if="topic.is_height_collapse" 
                  class="xx-small font-weight-bold d-inline-block ml-1 badge-collapse"
                  title="高度崩塌5→2：近5日最高连板>=5且前1日最高<=2，实盘V2风控排除该题材"
                >
                  崩盘5→2
                </b-badge>
                <b-badge 
                  v-if="!isTopicChecked(topic)" 
                  variant="secondary"
                  class="xx-small font-weight-bold d-inline-block ml-1 badge-excluded-12h"
                  :title="`该题材已取消勾选，12小时内不作为备选题材，期间不触发实盘监控买入（${getTopicRemainingHoursText(topic)}后自动恢复）`"
                >
                  <b-icon icon="slash-circle" class="mr-1"></b-icon>未作备选({{ getTopicRemainingHoursText(topic) }})
                </b-badge>
              </div>
              <div class="text-muted xx-small" v-if="topic.stats">
                涨跌: <span class="text-danger">{{ topic.stats.up_count }}</span>/<span class="text-success">{{ topic.stats.down_count }}</span>
                <span class="mx-1">|</span>
                均: <span :class="getPnlClass(topic.stats.avg_pct)">{{ topic.stats.avg_pct.toFixed(2) }}%</span>
              </div>
              <div class="text-muted xx-small mt-1" style="max-height: 2.8em; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.4;" v-if="topic.stats" :title="topic.stats.main_leader + ' (主) / ' + topic.stats.gem_leader + ' (创)'">
                领涨: <span class="leader-names">{{ topic.stats.main_leader }} / {{ topic.stats.gem_leader }}</span>
              </div>
              <div class="text-muted xx-small mt-1" style="max-height: 2.8em; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.4;" :title="topic.stats.limit_ups" v-if="topic.stats">
                涨停: <span class="text-danger">{{ topic.stats.limit_ups }}</span>
              </div>
            </div>
            <div class="text-right">
              <div class="text-muted xx-small">触发/限额</div>
              <div class="counts-text xx-small mt-1">
                <span class="text-danger font-weight-bold">{{ topic.alerts.length }}</span>次
                <span class="mx-1">|</span>
                主<span :class="{'text-warning': topic.counts['主板'] >= 2}">{{ topic.counts['主板'] || 0 }}/2</span>
                <span class="mx-1">|</span>
                创<span :class="{'text-warning': topic.counts['双创'] >= 1}">{{ topic.counts['双创'] || 0 }}/1</span>
              </div>
            </div>
          </div>
          
          <!-- 备选股列表 -->
          <div class="table-responsive">
            <table class="table panorama-table table-hover table-borderless table-sm mb-0 compact-table xx-small">
              <thead>
                <tr class="text-muted border-bottom panorama-divider">
                  <th style="width: 25%" class="sortable-th" @click="toggleMainCandidatesSort('name')" :title="getMainCandidatesSortTitle('name', '股票')">
                    <span>股票</span>
                    <span class="sort-indicator">
                      <b-icon v-if="mainSortKey === 'name'" :icon="mainSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                      <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                    </span>
                  </th>
                  <th class="text-right sortable-th" style="width: 13%" @click="toggleMainCandidatesSort('price')" :title="getMainCandidatesSortTitle('price', '现价')">
                    <span>现价</span>
                    <span class="sort-indicator">
                      <b-icon v-if="mainSortKey === 'price'" :icon="mainSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                      <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                    </span>
                  </th>
                  <th class="text-right text-nowrap sortable-th" style="width: 16%" @click="toggleMainCandidatesSort('amount')" :title="getMainCandidatesSortTitle('amount', '成交额')">
                    <span>额/值</span>
                    <span class="sort-indicator">
                      <b-icon v-if="mainSortKey === 'amount'" :icon="mainSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                      <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                    </span>
                  </th>
                  <th class="text-right text-nowrap sortable-th" style="width: 15%" @click="toggleMainCandidatesSort('pct_change')" :title="getMainCandidatesSortTitle('pct_change', '涨幅')">
                    <span>涨幅</span>
                    <span class="sort-indicator">
                      <b-icon v-if="mainSortKey === 'pct_change'" :icon="mainSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                      <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                    </span>
                  </th>
                  <th class="text-center text-nowrap" style="width: 31%">状态</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stock in getDisplayCandidates(topic.candidates, topic.topic_name)" :key="stock.code6">
                  <td class="align-middle text-nowrap">
                    <a href="javascript:void(0)" @click="showStockInfo(stock.code6, stock.name)" class="stock-name-link text-decoration-none hover-primary font-weight-bold text-truncate d-inline-block align-middle" style="max-width: 58px;" :title="stock.name + ' (' + stock.code6 + ')'">
                      {{ stock.name }}
                    </a>
                    <span
                      v-if="stock.score !== undefined && stock.score !== null"
                      class="score-pill ml-1 align-middle"
                      :class="getScoreClass(stock.score)"
                      :title="getScoreTooltip(stock)"
                    >
                      {{ stock.score > 0 ? '+' : '' }}{{ stock.score }}
                    </span>
                    <span class="text-muted ml-1 align-middle font-monospace" style="font-size: 0.6rem; letter-spacing: -0.2px;">{{ stock.code6 }}</span>
                  </td>
                  <td class="text-right align-middle font-weight-bold font-monospace" :class="getPnlClass(stock.pct_change)">
                    {{ stock.price.toFixed(2) }}
                  </td>
                  <td class="text-right align-middle text-muted text-nowrap font-monospace" style="font-size: 0.62rem;">
                    {{ formatAmountMkv(stock.amount, stock.mkv) }}
                  </td>
                  <td class="text-right align-middle font-weight-bold text-nowrap font-monospace" :class="getPnlClass(stock.pct_change)">
                    {{ stock.pct_change > 0 ? '+' : '' }}{{ stock.pct_change.toFixed(2) }}%
                  </td>
                  <td class="text-center align-middle py-0 px-1">
                    <div class="d-flex align-items-center justify-content-center text-nowrap" :title="getStatusTooltip(stock)">
                      <!-- 1. 智能合并单标签 (当 V2 与 V3 状态一致时，只展示一个精致单标签，消除80%冗余噪音) -->
                      <template v-if="isStatusMerged(stock)">
                        <b-badge :class="getMergedBadgeClass(stock)" class="status-badge-compact text-nowrap">
                          {{ getMergedBadgeText(stock) }}
                        </b-badge>
                      </template>

                      <!-- 2. 状态分化展示 (单行紧凑双胶囊，左右并列) -->
                      <template v-else>
                        <span class="micro-badge" :class="getV2BadgeClass(stock)">
                          <span class="micro-prefix v2-p">2:</span>{{ getV2BadgeText(stock) }}
                        </span>
                        <span class="micro-badge ml-1" :class="getV3BadgeClass(stock)">
                          <span class="micro-prefix v3-p">3:</span>{{ getV3BadgeText(stock) }}
                        </span>
                      </template>

                      <!-- 手动点火微型按钮 (仅今天且V2未买入/未触发时显示) -->
                      <b-button 
                        v-if="selectedDate === todayStr && isCanManualTrigger(stock)"
                        variant="link" 
                        size="sm" 
                        class="p-0 ml-1 fire-btn" 
                        title="手动点火买入"
                        @click.stop="confirmManualTrigger(stock.code6, stock.name)"
                      >
                        🔥
                      </b-button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="topic.candidates.length > 10" class="text-center mt-1">
              <b-button variant="link" size="sm" class="text-muted xx-small p-0" @click="toggleExpand(topic.topic_name)">
                {{ expandedTopics[topic.topic_name] ? '收起' : `查看更多 (${topic.candidates.length - 10})` }}
                <b-icon :icon="expandedTopics[topic.topic_name] ? 'chevron-up' : 'chevron-down'"></b-icon>
              </b-button>
            </div>
          </div>
        </b-card>
      </div>
    </div>
        </b-tab>
        <b-tab :title="`V2反弹备选 (${currentReboundCandidates.length})`">
            <div class="rebound-candidates-box rounded p-1">
              <div class="rebound-list-header xx-small text-muted">
                <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
                  <span>{{ selectedDate }} · {{ reboundStockRows.length }} 只股票 · {{ currentReboundCandidates.length }} 个题材 <template v-if="reboundOptimalConfig"> · 高点距昨日{{ reboundOptimalConfig.peak_age_min_days }}～{{ reboundOptimalConfig.peak_age_max_days }}交易日 · 波段{{ reboundOptimalConfig.prior_gain_min }}%≤涨幅&lt;{{ reboundOptimalConfig.prior_gain_max }}%</template></span>
                  <b-badge v-if="reboundSortKey" variant="warning" class="sort-active-badge cursor-pointer" @click="resetReboundSort" title="点击恢复默认排序">
                    按{{ reboundSortLabel }} {{ reboundSortOrder === 'asc' ? '升序 ↑' : '降序 ↓' }}
                    <b-icon icon="x" class="ml-1"></b-icon>
                  </b-badge>
                </div>
                <span title="候选展示不代表已买入，当前交易模式以页面上方状态为准。">备选观察 <b-icon icon="info-circle"></b-icon></span>
              </div>
              <details v-if="reboundOptimalConfig" class="rebound-topic-details xx-small mb-1">
                <summary>反弹最优参数 · {{ reboundOptimalConfig.peak_age_min_days }}～{{ reboundOptimalConfig.peak_age_max_days }}天 / 仓位{{ (reboundOptimalConfig.slot_exposure * 100).toFixed(0) }}% / 止损{{ reboundOptimalConfig.stoploss_pct }}% / 止盈{{ reboundOptimalConfig.trailing_stop_trigger }}%回撤{{ reboundOptimalConfig.trailing_stop_drop }}%</summary>
                <div class="text-muted px-1 py-1">
                  高点窗口{{ reboundOptimalConfig.peak_age_window }}交易日，波段涨幅≥{{ reboundOptimalConfig.prior_gain_min }}%且&lt;{{ reboundOptimalConfig.prior_gain_max }}%，先取Top{{ reboundOptimalConfig.top_n_leaders }}再过滤；题材回撤≤{{ reboundOptimalConfig.min_drawdown }}%。
                  最多持仓{{ reboundOptimalConfig.max_positions }}只，每日最多买入{{ reboundOptimalConfig.max_buy }}只，最长持有{{ reboundOptimalConfig.max_hold_days }}交易日，{{ reboundOptimalConfig.latest_buy_hm.slice(0, 2) }}:{{ reboundOptimalConfig.latest_buy_hm.slice(2) }}停止买入。
                  双创{{ reboundOptimalConfig.gem_buy_min }}%～{{ reboundOptimalConfig.gem_buy_max }}%，主板{{ reboundOptimalConfig.main_buy_min }}%～{{ reboundOptimalConfig.main_buy_max }}%；盘前已知市场5日均成交额≤{{ reboundOptimalConfig.volume_threshold }}亿元。
                  触发分钟价格加{{ reboundOptimalConfig.slippage_bps }}bp滑点，{{ reboundOptimalConfig.touch_limit_sell ? '摸涨停按涨停价卖出' : '不开启摸板卖出' }}，单边费用{{ reboundOptimalConfig.commission_pct }}%。
                </div>
                <div v-if="!(crowding && crowding.market_style && crowding.market_style.rebound_execution_enabled)" class="text-muted px-1 pb-1">候选筛选已应用；反弹自动买卖未启用，当前交易模式仍为V2主升。</div>
              </details>
              <div v-if="reboundStockRows.length" class="rebound-stock-grid">
                <div v-for="(group, groupIndex) in reboundStockGroups" :key="groupIndex" class="rebound-stock-group">
                  <div class="table-responsive">
                    <table class="table panorama-table table-hover table-borderless table-sm mb-0 xx-small compact-table rebound-stock-table">
                      <thead>
                        <tr class="text-muted border-bottom panorama-divider">
                          <th class="text-nowrap sortable-th" style="min-width: 108px;" @click="toggleReboundSort('code')" :title="getReboundSortTitle('code', '股票代码')">
                            <span>股票</span>
                            <span class="sort-indicator">
                              <b-icon v-if="reboundSortKey === 'code'" :icon="reboundSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                              <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                            </span>
                          </th>
                          <th class="text-right text-nowrap sortable-th" style="min-width: 48px;" @click="toggleReboundSort('price')" :title="getReboundSortTitle('price', '现价')">
                            <span>现价</span>
                            <span class="sort-indicator">
                              <b-icon v-if="reboundSortKey === 'price'" :icon="reboundSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                              <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                            </span>
                          </th>
                          <th class="text-right text-nowrap sortable-th" style="min-width: 60px;" @click="toggleReboundSort('amount')" :title="getReboundSortTitle('amount', '成交额') + '（成交额 / 流通市值）'">
                            <span>额/值</span>
                            <span class="sort-indicator">
                              <b-icon v-if="reboundSortKey === 'amount'" :icon="reboundSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                              <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                            </span>
                          </th>
                          <th class="text-right text-nowrap sortable-th" style="min-width: 50px;" @click="toggleReboundSort('pct_chg')" :title="getReboundSortTitle('pct_chg', '涨跌幅')">
                            <span>涨幅</span>
                            <span class="sort-indicator">
                              <b-icon v-if="reboundSortKey === 'pct_chg'" :icon="reboundSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                              <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                            </span>
                          </th>
                          <th class="text-right text-nowrap sortable-th" style="min-width: 52px;" @click="toggleReboundSort('prior_gain')" :title="getReboundSortTitle('prior_gain', '波段涨幅') + '（截至昨日近50日内最大波段涨幅）'">
                            <span>波段</span>
                            <span class="sort-indicator">
                              <b-icon v-if="reboundSortKey === 'prior_gain'" :icon="reboundSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                              <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                            </span>
                          </th>
                          <th class="text-right text-nowrap sortable-th" style="min-width: 40px;" @click="toggleReboundSort('peak_age_days')" :title="getReboundSortTitle('peak_age_days', '距最高点天数') + '（截至昨日收盘交易日数）'">
                            <span>距高</span>
                            <span class="sort-indicator">
                              <b-icon v-if="reboundSortKey === 'peak_age_days'" :icon="reboundSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                              <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                            </span>
                          </th>
                          <th class="text-center text-nowrap sortable-th" style="min-width: 76px;" @click="toggleReboundSort('status')" :title="getReboundSortTitle('status', '状态优先级')">
                            <span>状态</span>
                            <span class="sort-indicator">
                              <b-icon v-if="reboundSortKey === 'status'" :icon="reboundSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                              <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                            </span>
                          </th>
                          <th class="text-nowrap sortable-th" @click="toggleReboundSort('topic')" :title="getReboundSortTitle('topic', '所属题材数')">
                            <span>所属题材</span>
                            <span class="sort-indicator">
                              <b-icon v-if="reboundSortKey === 'topic'" :icon="reboundSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                              <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="stock in group" :key="stock.code">
                          <!-- 股票名称与代码同一行 -->
                          <td class="text-nowrap align-middle">
                            <div class="d-inline-flex align-items-baseline text-nowrap">
                              <button type="button" class="rebound-stock-link font-weight-bold" @click="showStockInfo(stock.code, stock.name)">
                                {{ stock.name || stock.code }}
                              </button>
                              <span class="text-muted ml-1 font-monospace rebound-stock-code">{{ stock.code }}</span>
                            </div>
                          </td>
                          <!-- 现价 -->
                          <td class="text-right text-nowrap align-middle font-monospace font-weight-bold" :title="stock.quoteDate ? `截至 ${stock.quoteDate} 收盘` : '所选日期行情'">
                            {{ formatReboundPrice(stock.price) }}
                          </td>
                          <!-- 额/值 -->
                          <td class="text-right text-nowrap align-middle text-muted font-monospace" style="font-size: 0.62rem;" :title="`成交额: ${formatReboundAmount(stock.amount)}亿 / 流通市值: ${formatReboundAmount(stock.mkv)}亿`">
                            {{ formatReboundAmount(stock.amount) }}/{{ formatReboundAmount(stock.mkv) }}
                          </td>
                          <!-- 涨幅 -->
                          <td class="text-right text-nowrap align-middle font-monospace font-weight-bold" :class="getPnlClass(stock.pct_chg)">
                            {{ formatReboundPct(stock.pct_chg) }}
                          </td>
                          <!-- 波段涨幅 -->
                          <td class="text-right text-nowrap align-middle font-monospace" :class="getPnlClass(stock.prior_gain)" title="截至昨日，最近50个交易日内复权价格先低后高的最大波段涨幅">
                            {{ formatReboundPct(stock.prior_gain) }}
                          </td>
                          <!-- 距高点 -->
                          <td class="text-right text-nowrap align-middle font-monospace text-muted" title="截至昨日收盘，按交易日计算">
                            {{ stock.peak_age_days === undefined || stock.peak_age_days === null ? '—' : `${stock.peak_age_days}天` }}
                          </td>
                          <!-- 状态 + 触发标记 + 手动点火 -->
                          <td class="text-center text-nowrap align-middle py-0 px-1">
                            <div class="d-inline-flex align-items-center justify-content-center text-nowrap" :title="getReboundStockStatus(stock).tooltip">
                              <b-badge :class="getReboundStockStatus(stock).badgeClass" class="status-badge-compact">
                                {{ getReboundStockStatus(stock).text }}
                              </b-badge>
                              <b-badge v-if="stock.is_limit_up" variant="danger" class="ml-1 status-badge-compact" title="已涨停">涨停</b-badge>
                              <!-- 手动点火微型按钮 (仅今天且未买入时显示) -->
                              <b-button 
                                v-if="selectedDate === todayStr && !isReboundStockBought(stock)"
                                variant="link" 
                                size="sm" 
                                class="p-0 ml-1 fire-btn" 
                                title="手动点火买入V2反弹"
                                @click.stop="openReboundRealBuy(stock)"
                              >
                                🔥
                              </b-button>
                            </div>
                          </td>
                          <!-- 所属题材 -->
                          <td class="rebound-topic-cell align-middle">
                            <span v-for="name in stock.topicNames" :key="name" class="rebound-topic-tag" :title="name">{{ name }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <details v-if="currentReboundCandidates.length" class="rebound-topic-details xx-small mt-2" open>
                <summary class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
                    <span><b-icon icon="layers" class="mr-1"></b-icon>题材筛选依据（{{ currentReboundCandidates.length }}）</span>
                    <b-badge v-if="reboundTopicSortKey" variant="warning" class="sort-active-badge cursor-pointer" @click.stop="resetReboundTopicSort" title="点击恢复默认排序">
                      按{{ reboundTopicSortLabel }} {{ reboundTopicSortOrder === 'asc' ? '升序 ↑' : '降序 ↓' }}
                      <b-icon icon="x" class="ml-1"></b-icon>
                    </b-badge>
                  </div>
                  <span class="text-muted font-weight-normal" style="font-size: 0.6rem;">点击可收起/展开</span>
                </summary>
                <div class="table-responsive mt-1">
                  <table class="table panorama-table table-sm table-borderless table-hover mb-0 xx-small compact-table">
                    <thead>
                      <tr class="text-muted border-bottom panorama-divider">
                        <th class="text-nowrap sortable-th" style="min-width: 130px;" @click="toggleReboundTopicSort('topic_name')" :title="getReboundTopicSortTitle('topic_name', '题材名称')">
                          <span>题材</span>
                          <span class="sort-indicator">
                            <b-icon v-if="reboundTopicSortKey === 'topic_name'" :icon="reboundTopicSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                            <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                          </span>
                        </th>
                        <th class="text-right text-nowrap sortable-th" style="min-width: 60px;" @click="toggleReboundTopicSort('today_pct')" :title="getReboundTopicSortTitle('today_pct', '今日涨幅')">
                          <span>涨幅</span>
                          <span class="sort-indicator">
                            <b-icon v-if="reboundTopicSortKey === 'today_pct'" :icon="reboundTopicSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                            <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                          </span>
                        </th>
                        <th class="text-right text-nowrap sortable-th" style="min-width: 60px;" @click="toggleReboundTopicSort('drawdown_pct')" :title="getReboundTopicSortTitle('drawdown_pct', '题材回撤')">
                          <span>回撤</span>
                          <span class="sort-indicator">
                            <b-icon v-if="reboundTopicSortKey === 'drawdown_pct'" :icon="reboundTopicSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                            <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                          </span>
                        </th>
                        <th v-if="hasReboundUpRatio" class="text-right text-nowrap sortable-th" style="min-width: 60px;" @click="toggleReboundTopicSort('up_ratio')" :title="getReboundTopicSortTitle('up_ratio', '上涨占比')">
                          <span>上涨占比</span>
                          <span class="sort-indicator">
                            <b-icon v-if="reboundTopicSortKey === 'up_ratio'" :icon="reboundTopicSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                            <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                          </span>
                        </th>
                        <th class="text-nowrap sortable-th" @click="toggleReboundTopicSort('gene_count')" :title="getReboundTopicSortTitle('gene_count', '强势标的数')">
                          <span>历史强势标的</span>
                          <span class="sort-indicator">
                            <b-icon v-if="reboundTopicSortKey === 'gene_count'" :icon="reboundTopicSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" class="sort-icon-active"></b-icon>
                            <b-icon v-else icon="arrow-down-up" class="sort-icon-idle"></b-icon>
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(topic, index) in sortedReboundCandidates" :key="topic.topic_id || index">
                        <td class="text-nowrap align-middle">
                          <button 
                            type="button" 
                            class="rebound-stock-link font-weight-bold" 
                            @click="showTopicKline(topic)" 
                            :title="'点击查看 ' + topic.topic_name + ' 题材K线图'"
                          >
                            {{ formatReboundTopicName(topic.topic_name) }}
                          </button>
                          <span v-if="topic.as_of_date" class="ml-1 text-muted font-monospace" style="font-size: 0.6rem; opacity: 0.85;">{{ topic.as_of_date }}</span>
                        </td>
                        <td class="text-right text-nowrap align-middle font-monospace font-weight-bold" :class="getPnlClass(topic.today_pct)">
                          {{ formatReboundPct(topic.today_pct) }}
                        </td>
                        <td class="text-right text-nowrap align-middle font-monospace text-muted" :class="topic.drawdown_pct < 0 ? 'text-success' : ''">
                          {{ formatReboundPct(topic.drawdown_pct) }}
                        </td>
                        <td v-if="hasReboundUpRatio" class="text-right text-nowrap align-middle font-monospace">
                          {{ formatReboundPct(topic.up_ratio) }}
                        </td>
                        <td class="align-middle">
                          <div class="d-flex flex-wrap align-items-center" style="gap: 4px;">
                            <button
                              type="button"
                              v-for="item in getTopicGeneStocks(topic)"
                              :key="item.name"
                              class="rebound-gene-btn text-nowrap"
                              :title="`点击查看 ${item.name} K线图 (波段涨幅: ${item.gainStr})`"
                              @click.stop="openGeneStockKline(item)"
                            >
                              <b-icon icon="graph-up" class="mr-1 opacity-70" style="font-size: 0.58rem;"></b-icon>
                              <span class="font-weight-bold">{{ item.name }}</span>
                              <span v-if="item.gainStr" class="ml-1 text-danger font-monospace" style="font-size: 0.6rem;">({{ item.gainStr }})</span>
                            </button>
                            <span v-if="!getTopicGeneStocks(topic).length" class="text-muted">—</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </details>
              <div v-else class="text-muted xx-small py-2">{{ (crowding && crowding.market_style && crowding.market_style.rebound_candidates_error) || `${selectedDate} 暂无符合条件的反弹备选` }}</div>
            </div>
        </b-tab>
      </b-tabs>
    </section>

    <!-- 引入个股侧边栏 -->
    <StockInfoSidebar 
      :symbol_code="sidebarSymbolCode" 
      :symbol_name="sidebarSymbolName" 
    />

    <!-- 题材K线图模态框 -->
    <TopicKlineModal
      :visible="showTopicKlineModal"
      :uniqueKey="selectedTopicUniqueKey"
      :topicName="selectedTopicName"
      @close="showTopicKlineModal = false"
    />

    <!-- 实盘买入点火模态弹窗 -->
    <b-modal
      id="real-buy-modal"
      v-model="showRealBuyModal"
      title="🔥 实盘买入点火确认"
      :header-bg-variant="isDarkMode ? 'dark' : 'light'"
      :header-text-variant="isDarkMode ? 'light' : 'dark'"
      :body-bg-variant="isDarkMode ? 'dark' : 'light'"
      :body-text-variant="isDarkMode ? 'light' : 'dark'"
      :footer-bg-variant="isDarkMode ? 'dark' : 'light'"
      :footer-text-variant="isDarkMode ? 'light' : 'dark'"
      centered
      no-close-on-backdrop
    >
      <div class="real-buy-modal-content">
        <!-- 标的概要 -->
        <div class="target-summary-card p-3 rounded mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <div class="h5 mb-0 text-title font-weight-bold">
                {{ realBuyForm.name }}
                <span class="text-muted-gold ml-2 font-monospace" style="font-size: 0.95rem;">{{ realBuyForm.code }}</span>
              </div>
              <div class="xx-small text-muted mt-1">模拟买入触发标的 ｜ 准备向券商实盘通道提交买入委托</div>
            </div>
            <b-badge variant="danger" class="px-2 py-1">实盘点火</b-badge>
          </div>
        </div>

        <!-- 选择实盘策略 -->
        <div class="form-group mb-3">
          <label class="font-weight-bold text-title d-flex justify-content-between align-items-center">
            <span>选择实盘策略 <span class="text-danger">*</span></span>
            <span class="xx-small text-muted font-weight-normal">默认匹配模拟触发策略</span>
          </label>
          <div class="d-flex flex-wrap strategy-select-group">
            <b-button
              class="flex-fill mr-2 py-2 text-center"
              :variant="realBuyForm.strategy === 'v2' ? 'warning' : 'outline-secondary'"
              @click="onRealBuyStrategyChange('v2')"
            >
              <div class="font-weight-bold">实盘 V2</div>
              <div class="xx-small mt-1 opacity-80">当前配置总仓位 {{ positionTotal(v2RealPositionPcts) }}%</div>
            </b-button>
            <b-button
              class="flex-fill py-2 text-center"
              :variant="realBuyForm.strategy === 'v3' ? 'info' : 'outline-secondary'"
              @click="onRealBuyStrategyChange('v3')"
            >
              <div class="font-weight-bold">实盘 V3</div>
              <div class="xx-small mt-1 opacity-80">当前配置总仓位 {{ positionTotal(v3RealPositionPcts) }}%</div>
            </b-button>
            <b-button class="flex-fill ml-2 py-2" :variant="realBuyForm.strategy === 'rebound' ? 'info' : 'outline-secondary'" @click="onRealBuyStrategyChange('rebound')"><div class="font-weight-bold">V2反弹</div><div class="xx-small mt-1">{{ positionTotal(reboundRealGroups[realBuyForm.reboundGroup || 'v2'].pcts) }}%</div></b-button>
          </div>
        </div>

        <div v-if="realBuyForm.strategy === 'rebound'" class="mb-2">
        <label class="small">反弹实盘对接组</label>
        <b-form-select v-model="realBuyForm.reboundGroup" size="sm" :options="[{ value: 'v2', text: 'V2实盘组' }, { value: 'v3', text: 'V3实盘组' }]" @change="onRealBuyStrategyChange('rebound')"></b-form-select>
      </div>
      <!-- 调整买入仓位 -->
        <div class="form-group mb-3">
          <label class="font-weight-bold text-title d-flex justify-content-between align-items-center">
            <span>买入仓位比例 <span class="text-danger">*</span></span>
            <span class="xx-small text-muted font-weight-normal">
              默认配置: {{ getDefaultRealPositionPct(realBuyForm.strategy, realBuyForm.targetIdx) }}%
            </span>
          </label>
          <b-input-group append="%">
            <b-form-input
              v-model.number="realBuyForm.positionPct"
              type="number"
              min="0.1"
              max="100"
              step="0.5"
              placeholder="请输入买入仓位比例"
            ></b-form-input>
          </b-input-group>

          <!-- 常用快捷仓位选择 -->
          <div class="d-flex align-items-center flex-wrap mt-2">
            <span class="xx-small text-muted mr-2">快捷:</span>
            <b-button
              v-for="p in [5, 10, 15, 20]"
              :key="p"
              size="sm"
              variant="outline-secondary"
              class="px-2 py-0 mr-1 mb-1 xx-small"
              :class="{ 'btn-info text-white': Number(realBuyForm.positionPct) === p }"
              @click="realBuyForm.positionPct = p"
            >
              {{ p }}%
            </b-button>
            <b-button
              size="sm"
              variant="outline-warning"
              class="px-2 py-0 mb-1 xx-small"
              @click="realBuyForm.positionPct = getDefaultRealPositionPct(realBuyForm.strategy, realBuyForm.targetIdx)"
            >
              恢复默认
            </b-button>
          </div>
        </div>

        <div class="form-group mb-3 p-2 rounded border border-secondary-40 bg-dark-pill">
          <b-form-checkbox v-model="realBuyForm.blockExistingPosition" :disabled="submittingRealBuy">
            拦截已持仓股票
          </b-form-checkbox>
          <div class="xx-small mt-1" :class="realBuyPosition && realBuyPosition.held ? 'text-warning' : 'text-muted'">
            {{ realBuyPositionMessage }}
          </div>
          <b-button v-if="realBuyPositionError" variant="link" size="sm" class="p-0 xx-small" @click="checkRealBuyPosition">重新查询</b-button>
          <div v-if="!realBuyForm.blockExistingPosition" class="xx-small text-warning mt-1">已取消持仓拦截，本次允许继续加仓。</div>
        </div>

        <!-- 是否发送通知 -->
        <div class="form-group mb-3 d-flex align-items-center justify-content-between p-2 rounded border border-secondary-40 bg-dark-pill">
          <div>
            <div class="font-weight-bold text-title" style="font-size: 0.85rem;">
              <b-icon icon="bell" class="mr-1 text-warning"></b-icon>
              发送微信通知
            </div>
            <div class="xx-small text-muted mt-0">
              点火提交后向微信模板与 VIP 群推送买入通知（默认不发送）
            </div>
          </div>
          <b-form-checkbox
            v-model="realBuyForm.sendNotification"
            switch
            class="custom-dark-switch xx-small"
          >
            <span :class="realBuyForm.sendNotification ? 'text-warning font-weight-bold' : 'text-muted'" style="font-size: 0.8rem; cursor: pointer; user-select: none;">
              {{ realBuyForm.sendNotification ? '发送' : '不发送' }}
            </span>
          </b-form-checkbox>
        </div>

        <!-- 风险提示 -->
        <div class="alert alert-warning p-2 xx-small mb-0 border-0 bg-soft-warning">
          <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
          <strong>风险警示：</strong>点击确认后，系统将直接向券商外部通道提交实盘买入委托指令{{ realBuyForm.sendNotification ? '并推送微信通知' : '（不推送微信通知）' }}。请核对标的及仓位无误后操作！
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" size="sm" class="px-3" :disabled="submittingRealBuy" @click="cancel()">
          取消
        </b-button>
        <b-button variant="danger" size="sm" class="px-4 font-weight-bold" :disabled="submittingRealBuy || realBuyPositionBlocked" @click="submitRealBuy">
          <b-spinner v-if="submittingRealBuy" small class="mr-1"></b-spinner>
          确认点火买入
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'
import TopicKlineModal from '@/components/plate/TopicKlineModal.vue'

export default {
  name: 'WonderStrategyPanorama',
  components: {
    StockInfoSidebar,
    TopicKlineModal
  },
  data() {
    const d = new Date()
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const todayStr = `${year}-${month}-${day}`
    return {
      loading: true,
      candidateTab: 0,
      topics: [],
      expandedTopics: {},
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      showTopicKlineModal: false,
      selectedTopicUniqueKey: '',
      selectedTopicName: '',
      selectedDate: todayStr,
      v2AbGroup: null,
      supplementH67: false,
      filterTopicNet5: true,
      v2PositionPcts: [25, 25],
      v2RealPositionPcts: [25, 25],
      v3PositionPcts: [15, 15, 8, 7, 5],
      v3RealPositionPcts: [15, 15, 8, 7, 5],
      reboundPositionPcts: [40, 40],
      reboundRealPositionPcts: [40, 40],
      reboundRealGroups: { v2: { enabled: false, pcts: [40, 40] }, v3: { enabled: false, pcts: [40, 40] } },
      reboundAccountId: 5,
      v2MarketMode: 'main',
      v2ActiveMode: 'main',
      switchingReason: '',
      reboundCandidates: [],
      reboundQuotes: {},
      reboundBuys: {},
      reboundHoldingCodes: [],
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      crowding: { available: false, level: 'unknown' },
      todayBuys: { v2: [], v3: [], rebound: [] },
      showRealBuyModal: false,
      submittingRealBuy: false,
      realBuyPosition: null,
      realBuyPositionLoading: false,
      realBuyPositionError: false,
      realBuyPositionRequest: 0,
      realBuyForm: {
        code: '',
        name: '',
        price: null,
        strategy: 'v2',
        reboundGroup: 'v2',
        positionPct: 5.0,
        targetIdx: 0,
        sendNotification: false,
        blockExistingPosition: true
      },
      savingConfig: false,
      positionConfigExpanded: false,
      refreshing: false,
      autoSync: false,
      lastSyncTime: '',
      silentSyncing: false,
      pendingSyncOnVisible: false,
      ws: null,
      wsReconnectTimer: null,
      isDarkMode: false,
      themeObserver: null,
      resolvedStockCodes: {},
      reboundSortKey: null,
      reboundSortOrder: null,
      reboundTopicSortKey: null,
      reboundTopicSortOrder: null,
      mainSortKey: null,
      mainSortOrder: null,
      topicCheckMap: {},
      currentTimeSec: Math.floor(Date.now() / 1000),
      topicCheckTimer: null
    }
  },
  computed: {
    checkedTopicsCount() {
      if (!Array.isArray(this.topics)) return 0
      return this.topics.filter(t => this.isTopicChecked(t)).length
    },
    realBuyPositionBlocked() {
      return this.realBuyForm.blockExistingPosition && (this.realBuyPositionLoading || !this.realBuyPosition || this.realBuyPosition.held)
    },
    realBuyPositionMessage() {
      if (this.realBuyPositionLoading) return '正在查询策略账本持仓…'
      if (this.realBuyPositionError || !this.realBuyPosition) return '持仓状态未确认，默认拦截；可重新查询。'
      if (this.realBuyPosition.held) return `已有持仓：${this.realBuyPosition.holdings.map(item => `${item.strategy} ${item.quantity}股`).join('、')}。取消勾选后可继续购买。`
      return '对应策略组账本未发现持仓。'
    },
    v2AbGroupLabel() {
      const info = this.v2AbGroup
      if (!info || info.date !== this.selectedDate) return 'V2 分组待确认'
      if (info.status === 'ok' && ['A', 'B'].includes(info.group)) return `V2 当日：${info.group}组`
      if (info.status === 'non_trading_day') return 'V2 非交易日'
      if (info.status === 'before_anchor') return 'V2 暂无分组'
      return 'V2 分组待确认'
    },
    crowdingBadgeVariant() {
      return { blocked: 'danger', reduced: 'warning', normal: 'success' }[this.crowding.level] || 'secondary'
    },
    crowdingLevelText() {
      return { blocked: '禁止开仓', reduced: '建议半仓', normal: '正常' }[this.crowding.level] || '暂无数据'
    },
    todayStr() {
      const d = new Date()
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    isCurrentDateToday() {
      return this.selectedDate === this.todayStr
    },
    marketStyleRegimeVariant() {
      const style = this.crowding && this.crowding.market_style
      if (!style) return 'secondary'
      return style.volume_regime === 'bull' ? 'danger' : 'warning'
    },
    overallGateBlocked() {
      const style = this.crowding && this.crowding.market_style
      if (!style) return false
      return style.overall_gate_action === 'block' || style.timing_gate_action === 'block' || style.weak_low_volume_gate_action === 'block'
    },
    overallGateStatusText() {
      const style = this.crowding && this.crowding.market_style
      if (!style) return '正常'
      if (style.overall_gate_status) return style.overall_gate_status
      if (style.weak_low_volume_gate_action === 'block') return '触发暂停新开仓'
      if (style.timing_gate_action === 'block') return '触发防守熔断'
      return '准入通行'
    },
    overallGateVariant() {
      return this.overallGateBlocked ? 'danger' : 'success'
    },
    overallGateStatusTooltip() {
      const style = this.crowding && this.crowding.market_style
      if (!style) return ''
      if (style.weak_low_volume_gate_action === 'block') {
        const toThresh = style.weak_timing_turnover_threshold ? (style.weak_timing_turnover_threshold / 10000).toFixed(2) : '1.80'
        return `弱势低量拦截：Timing=${style.market_timing}，成交额低于${toThresh}万亿，已暂停全部V2新开仓`
      }
      if (style.timing_gate_action === 'block') {
        return `情绪防守熔断：Timing=${style.market_timing}属于高危阶段，已排除买入`
      }
      return '情绪与量能风控均正常通行'
    },
    marketStyleGateVariant() {
      return this.overallGateVariant
    },
    marketStyleSummaryClass() {
      const style = this.crowding && this.crowding.market_style
      if (!style) return 'style-summary-normal'
      if (this.overallGateBlocked) return 'style-summary-blocked'
      return 'style-summary-bull'
    },
    weakTimingLowVolumeEnabled() {
      const style = this.crowding && this.crowding.market_style
      return Boolean(style && style.weak_timing_low_volume)
    },
    weakTimingLowVolumeBlocked() {
      const style = this.crowding && this.crowding.market_style
      return style && style.weak_low_volume_gate_action === 'block'
    },
    weakTimingLowVolumeVariant() {
      return this.weakTimingLowVolumeBlocked ? 'danger' : 'success'
    },
    weakTimingLowVolumeStatusText() {
      const style = this.crowding && this.crowding.market_style
      return (style && style.weak_low_volume_gate_status) || '未知'
    },
    weakTimingMax() {
      const style = this.crowding && this.crowding.market_style
      const value = Number(style && style.weak_timing_max)
      return Number.isFinite(value) ? value : -2
    },
    weakTimingValueText() {
      const style = this.crowding && this.crowding.market_style
      if (!style || style.market_timing === undefined || style.market_timing === null) return '未知'
      return String(style.market_timing)
    },
    weakTimingTurnoverText() {
      const style = this.crowding && this.crowding.market_style
      const value = Number(style && style.prev_turnover_bi)
      return Number.isFinite(value) && value > 0 ? `${(value / 10000).toFixed(2)}万亿` : '缺失'
    },
    weakTimingTurnoverThreshold() {
      const style = this.crowding && this.crowding.market_style
      const value = Number(style && style.weak_timing_turnover_threshold)
      return Number.isFinite(value) && value > 0 ? value : 18000
    },
    weakTimingTurnoverThresholdText() {
      return `${(this.weakTimingTurnoverThreshold / 10000).toFixed(2)}万亿`
    },
    weakTimingLowVolumeActionText() {
      return this.weakTimingLowVolumeBlocked ? '暂停' : '正常评估'
    },
    climaxDefenseHelpText() {
      return '高潮防守需要同时满足两项：候选信号确定性评分≥4，且前日Timing在3/4/5/6。当前评分器中Timing 3/4计-1分，其它四项最多+4，因此最高3分，不会触发；Timing 5/6计0分，其它四项全部命中可得4分，因此当前实际可触发阶段是Timing 5/6。保留3/4是为了兼容未来评分规则调整。Timing 1/2或≤0不属于该防守阶段，不会因此拦截。'
    },
    summaryClimaxStatusHelpText() {
      const style = this.crowding && this.crowding.market_style
      const timing = style && style.market_timing
      const timingLabel = timing === undefined || timing === null ? '未知' : timing
      return `当前指引为什么显示“正常准入”：前日Timing=${timingLabel}，不在3/4/5/6防守阶段，所以无论确定性评分多少，都不会因高潮防守拦截。补充说明：当前评分器下Timing 3/4最高只有3分，实际无法触发；Timing 5/6在其它四项全部命中时可达4分，才会显示“Score≥4盛极必衰跳过”。当前状态仍会继续检查弱势低量等其它风控。`
    },
    marketStyleSummaryParts() {
      const style = this.crowding && this.crowding.market_style
      const summary = (style && (style.summary || style.switching_reason)) || ''
      const climaxStatus = style && style.climax_defense_status
      if (!summary || !climaxStatus) {
        return { before: summary, climax: '', after: '' }
      }
      const climaxIndex = summary.lastIndexOf(`；${climaxStatus}`)
      const statusEnd = climaxIndex + 1 + climaxStatus.length
      const suffix = summary.slice(statusEnd)
      if (climaxIndex < 0 || (suffix && suffix !== '。' && !suffix.startsWith('；'))) {
        return { before: summary, climax: '', after: '' }
      }
      return {
        before: summary.slice(0, climaxIndex + 1),
        climax: climaxStatus,
        after: suffix
      }
    },
    effectiveActiveMode() {
      return this.v2ActiveMode
    },
    effectiveActiveModeText() {
      return this.effectiveActiveMode === 'rebound' ? 'V2反弹实盘' : 'V2主升实盘'
    },
    activeModeBadgeVariant() {
      return this.effectiveActiveMode === 'rebound' ? 'info' : 'danger'
    },
    reboundOptimalConfig() {
      return this.crowding && this.crowding.market_style && this.crowding.market_style.rebound_optimal_config
    },
    sortedReboundStockRows() {
      const rows = this.reboundStockRows.slice()
      if (!this.reboundSortKey || !this.reboundSortOrder) return rows
      const key = this.reboundSortKey
      const isAsc = this.reboundSortOrder === 'asc'

      return rows.sort((a, b) => {
        let cmp = 0
        if (key === 'code') {
          cmp = (a.code || '').localeCompare(b.code || '')
          return isAsc ? cmp : -cmp
        }
        if (key === 'price') {
          const aVal = Number(a.price)
          const bVal = Number(b.price)
          const aValid = a.price !== null && a.price !== undefined && !Number.isNaN(aVal)
          const bValid = b.price !== null && b.price !== undefined && !Number.isNaN(bVal)
          if (!aValid && !bValid) cmp = 0
          else if (!aValid) return 1
          else if (!bValid) return -1
          else cmp = isAsc ? (aVal - bVal) : (bVal - aVal)
        } else if (key === 'amount') {
          const aVal = Number(a.amount)
          const bVal = Number(b.amount)
          const aValid = a.amount !== null && a.amount !== undefined && !Number.isNaN(aVal)
          const bValid = b.amount !== null && b.amount !== undefined && !Number.isNaN(bVal)
          if (!aValid && !bValid) cmp = 0
          else if (!aValid) return 1
          else if (!bValid) return -1
          else cmp = isAsc ? (aVal - bVal) : (bVal - aVal)
        } else if (key === 'pct_chg') {
          const aVal = Number(a.pct_chg)
          const bVal = Number(b.pct_chg)
          const aValid = a.pct_chg !== null && a.pct_chg !== undefined && !Number.isNaN(aVal)
          const bValid = b.pct_chg !== null && b.pct_chg !== undefined && !Number.isNaN(bVal)
          if (!aValid && !bValid) cmp = 0
          else if (!aValid) return 1
          else if (!bValid) return -1
          else cmp = isAsc ? (aVal - bVal) : (bVal - aVal)
        } else if (key === 'prior_gain') {
          const aVal = Number(a.prior_gain)
          const bVal = Number(b.prior_gain)
          const aValid = a.prior_gain !== null && a.prior_gain !== undefined && !Number.isNaN(aVal)
          const bValid = b.prior_gain !== null && b.prior_gain !== undefined && !Number.isNaN(bVal)
          if (!aValid && !bValid) cmp = 0
          else if (!aValid) return 1
          else if (!bValid) return -1
          else cmp = isAsc ? (aVal - bVal) : (bVal - aVal)
        } else if (key === 'peak_age_days') {
          const aVal = Number(a.peak_age_days)
          const bVal = Number(b.peak_age_days)
          const aValid = a.peak_age_days !== null && a.peak_age_days !== undefined && !Number.isNaN(aVal)
          const bValid = b.peak_age_days !== null && b.peak_age_days !== undefined && !Number.isNaN(bVal)
          if (!aValid && !bValid) cmp = 0
          else if (!aValid) return 1
          else if (!bValid) return -1
          else cmp = isAsc ? (aVal - bVal) : (bVal - aVal)
        } else if (key === 'status') {
          const priorityMap = { bought: 1, limit_up: 2, triggered: 3, holding: 4, over: 5, untriggered: 6 }
          const sa = this.getReboundStockStatus(a)
          const sb = this.getReboundStockStatus(b)
          const pa = priorityMap[sa.status] ?? 99
          const pb = priorityMap[sb.status] ?? 99
          cmp = isAsc ? (pb - pa) : (pa - pb)
        } else if (key === 'topic') {
          const lenA = (a.topicNames || []).length
          const lenB = (b.topicNames || []).length
          cmp = isAsc ? (lenA - lenB) : (lenB - lenA)
        }

        if (cmp !== 0) return cmp
        return (a.code || '').localeCompare(b.code || '')
      })
    },
    reboundStockGroups() {
      const count = this.viewportWidth >= 1400 && this.sortedReboundStockRows.length >= 8 ? 2 : 1
      const size = Math.ceil(this.sortedReboundStockRows.length / count)
      if (!size) return []
      return Array.from({ length: count }, (_, index) => this.sortedReboundStockRows.slice(index * size, (index + 1) * size)).filter(group => group.length)
    },
    sortedReboundCandidates() {
      const list = this.currentReboundCandidates.slice()
      if (!this.reboundTopicSortKey || !this.reboundTopicSortOrder) return list
      const key = this.reboundTopicSortKey
      const isAsc = this.reboundTopicSortOrder === 'asc'

      return list.sort((a, b) => {
        let cmp = 0
        if (key === 'topic_name') {
          const na = a.topic_name || ''
          const nb = b.topic_name || ''
          cmp = na.localeCompare(nb, 'zh-CN')
          return isAsc ? cmp : -cmp
        }
        if (key === 'today_pct') {
          const aVal = Number(a.today_pct)
          const bVal = Number(b.today_pct)
          const aValid = a.today_pct !== null && a.today_pct !== undefined && !Number.isNaN(aVal)
          const bValid = b.today_pct !== null && b.today_pct !== undefined && !Number.isNaN(bVal)
          if (!aValid && !bValid) cmp = 0
          else if (!aValid) return 1
          else if (!bValid) return -1
          else cmp = isAsc ? (aVal - bVal) : (bVal - aVal)
        } else if (key === 'drawdown_pct') {
          const aVal = Number(a.drawdown_pct)
          const bVal = Number(b.drawdown_pct)
          const aValid = a.drawdown_pct !== null && a.drawdown_pct !== undefined && !Number.isNaN(aVal)
          const bValid = b.drawdown_pct !== null && b.drawdown_pct !== undefined && !Number.isNaN(bVal)
          if (!aValid && !bValid) cmp = 0
          else if (!aValid) return 1
          else if (!bValid) return -1
          else cmp = isAsc ? (aVal - bVal) : (bVal - aVal)
        } else if (key === 'up_ratio') {
          const aVal = Number(a.up_ratio)
          const bVal = Number(b.up_ratio)
          const aValid = a.up_ratio !== null && a.up_ratio !== undefined && !Number.isNaN(aVal)
          const bValid = b.up_ratio !== null && b.up_ratio !== undefined && !Number.isNaN(bVal)
          if (!aValid && !bValid) cmp = 0
          else if (!aValid) return 1
          else if (!bValid) return -1
          else cmp = isAsc ? (aVal - bVal) : (bVal - aVal)
        } else if (key === 'gene_count') {
          const lenA = this.getTopicGeneStocks(a).length
          const lenB = this.getTopicGeneStocks(b).length
          cmp = isAsc ? (lenA - lenB) : (lenB - lenA)
        }

        if (cmp !== 0) return cmp
        return (a.topic_name || '').localeCompare(b.topic_name || '', 'zh-CN')
      })
    },
    reboundSortLabel() {
      const map = {
        code: '股票代码',
        price: '现价',
        amount: '成交额',
        pct_chg: '涨跌幅',
        prior_gain: '波段涨幅',
        peak_age_days: '距高天数',
        status: '状态',
        topic: '题材数'
      }
      return map[this.reboundSortKey] || ''
    },
    reboundTopicSortLabel() {
      const map = {
        topic_name: '题材名称',
        today_pct: '涨幅',
        drawdown_pct: '回撤',
        up_ratio: '上涨占比',
        gene_count: '强势标的数'
      }
      return map[this.reboundTopicSortKey] || ''
    },
    mainCandidatesSortLabel() {
      const map = {
        name: '股票名称',
        price: '现价',
        amount: '成交额',
        pct_change: '涨幅'
      }
      return map[this.mainSortKey] || ''
    },
    hasReboundUpRatio() {
      return this.currentReboundCandidates.some(t => t.up_ratio !== null && t.up_ratio !== undefined && t.up_ratio !== '' && Number.isFinite(Number(t.up_ratio)))
    },
    reboundStockRows() {
      const quotes = new Map()
      this.topics.forEach(topic => (topic.candidates || []).forEach(stock => {
        quotes.set(String(stock.code6 || stock.code || stock.ts_code).split('.')[0].padStart(6, '0'), stock)
      }))
      const rows = new Map()
      this.currentReboundCandidates.forEach(topic => {
        const topicName = this.formatReboundTopicName(topic.topic_name)
        this.getReboundStocks(topic).forEach(stock => {
          if (!rows.has(stock.code)) {
            const quote = this.isCurrentDateToday ? (this.reboundQuotes[stock.code] || quotes.get(stock.code)) : null
            rows.set(stock.code, { ...stock,
              price: quote ? quote.price : this.isCurrentDateToday ? null : stock.price,
              amount: quote ? quote.amount : this.isCurrentDateToday ? null : stock.amount,
              mkv: quote ? quote.mkv : this.isCurrentDateToday ? null : stock.mkv,
              pct_chg: quote ? (quote.pct_chg === undefined ? quote.pct_change : quote.pct_chg) : this.isCurrentDateToday ? null : stock.pct_chg,
              is_limit_up: !this.isCurrentDateToday && stock.is_limit_up,
              quoteDate: this.isCurrentDateToday ? null : topic.as_of_date,
              topicNames: []
            })
          }
          const row = rows.get(stock.code)
          if (topicName && !row.topicNames.includes(topicName)) row.topicNames.push(topicName)
        })
      })
      return Array.from(rows.values())
    },
    currentReboundCandidates() {
      const style = this.crowding && this.crowding.market_style
      if (style && Array.isArray(style.rebound_candidates)) {
        return style.rebound_candidates
      }
      return this.reboundCandidates || []
    },
    stockNameCodeMap() {
      const map = {}
      if (Array.isArray(this.reboundStockRows)) {
        this.reboundStockRows.forEach(s => {
          if (s && s.name && s.code) map[s.name] = s.code
        })
      }
      if (Array.isArray(this.topics)) {
        this.topics.forEach(t => {
          (t.candidates || []).forEach(s => {
            const code = s.code6 || s.code || s.ts_code
            if (s && s.name && code) {
              map[s.name] = String(code).split('.')[0].padStart(6, '0')
            }
          })
        })
      }
      if (Array.isArray(this.currentReboundCandidates)) {
        this.currentReboundCandidates.forEach(t => {
          (t.candidates || []).forEach(s => {
            if (s && s.name && s.code) map[s.name] = s.code
          });
          (t.gene_stocks || []).forEach(s => {
            if (s && s.name && s.code) map[s.name] = s.code
          })
        })
      }
      return map
    }
  },
  created() {
    this.loadTopicExclusionsFromLocal()
    const savedAutoSync = localStorage.getItem('panorama_auto_sync')
    if (savedAutoSync === 'true') {
      this.autoSync = true
    }
    this.initializePage()
  },
  watch: {
    showRealBuyModal(visible) {
      if (visible) {
        this.$set(this.realBuyForm, 'blockExistingPosition', true)
        this.checkRealBuyPosition()
      } else {
        this.realBuyPositionRequest++
      }
    },
    'realBuyForm.strategy'() {
      if (this.showRealBuyModal) this.checkRealBuyPosition()
    },
    'realBuyForm.reboundGroup'() {
      if (this.showRealBuyModal) this.checkRealBuyPosition()
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateViewportWidth)
    this.checkTheme()
    if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
      this.themeObserver = new MutationObserver(() => {
        this.checkTheme()
      })
      this.themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    if (this.autoSync && this.isCurrentDateToday) {
      this.initWebSocket()
    }
    this.topicCheckTimer = setInterval(() => {
      this.currentTimeSec = Math.floor(Date.now() / 1000)
    }, 30000)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewportWidth)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    if (this.topicCheckTimer) {
      clearInterval(this.topicCheckTimer)
      this.topicCheckTimer = null
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect()
      this.themeObserver = null
    }
    this.closeWebSocket()
  },
  methods: {
    updateViewportWidth() {
      this.viewportWidth = window.innerWidth
    },
    formatReboundTopicName(value) {
      return String(value || '').replace(/\s*[（(][^()（）]*[)）]\s*$/, '').trim()
    },
    getTopicGeneStocks(topic) {
      if (!topic) return []
      if (Array.isArray(topic.gene_stocks) && topic.gene_stocks.length > 0) {
        return topic.gene_stocks.map(s => ({
          code: s.code || this.stockNameCodeMap[s.name] || this.resolvedStockCodes[s.name] || '',
          name: s.name,
          gainStr: s.prior_gain !== undefined && s.prior_gain !== null ? `+${s.prior_gain}%` : ''
        }))
      }
      if (!topic.historical_gene) return []
      const raw = String(topic.historical_gene).replace(/^前期强势标的[：:]\s*/, '')
      const parts = raw.split(/[、,，]/).map(p => p.trim()).filter(Boolean)
      return parts.map(part => {
        const m = part.match(/^([^（(]+)(?:[（(]([^）)]+)[）)])?/)
        const name = m ? m[1].trim() : part
        const gainStr = m && m[2] ? m[2].trim() : ''
        const code = this.stockNameCodeMap[name] || this.resolvedStockCodes[name] || ''
        return { name, code, gainStr }
      })
    },
    async openGeneStockKline(item) {
      if (!item || !item.name) return
      let code = item.code || this.stockNameCodeMap[item.name] || this.resolvedStockCodes[item.name]
      if (!code) {
        try {
          const res = await request_zzquant({
            url: '/v3/topic/stock/resolve-code',
            method: 'get',
            params: { name: item.name }
          })
          if (res.data && res.data.code === 200 && res.data.data && res.data.data.code) {
            code = res.data.data.code
            this.$set(this.resolvedStockCodes, item.name, code)
          }
        } catch (e) {
          console.error('反查股票代码失败:', e)
        }
      }
      if (code) {
        this.showStockInfo(code, item.name)
      } else {
        this.$bvToast.toast(`未能匹配到 ${item.name} 的股票代码`, {
          title: '提示',
          variant: 'warning',
          solid: true
        })
      }
    },
    formatReboundPrice(value) {
      return value === null || value === undefined || !Number.isFinite(Number(value)) || Number(value) <= 0 ? '—' : Number(value).toFixed(2)
    },
    formatReboundAmount(value) {
      return value === null || value === undefined || !Number.isFinite(Number(value)) ? '—' : (Number(value) / 100000000).toFixed(1)
    },
    getReboundStocks(topic) {
      const stocks = Array.isArray(topic.candidates) ? topic.candidates
        : Array.isArray(topic.leader_stocks) ? topic.leader_stocks
          : topic.leader_stock ? [topic.leader_stock] : []
      return stocks.filter(stock => stock && (stock.code || stock.code6 || stock.ts_code)).map(stock => ({
        ...stock,
        code: String(stock.code || stock.code6 || stock.ts_code).split('.')[0].padStart(6, '0'),
        pct_chg: stock.pct_chg === undefined ? stock.pct_change : stock.pct_chg
      }))
    },
    formatReboundPct(value) {
      if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '—'
      return `${Number(value).toFixed(2)}%`
    },
    isReboundStockBought(stock) {
      if (!stock) return false
      const buyTime = this.reboundBuys?.[stock.code] || stock.buy_time
      if (buyTime || stock.bought) return true
      const reboundTodayBuys = this.todayBuys?.rebound || []
      return reboundTodayBuys.some(item => (item.code || '').startsWith(stock.code))
    },
    getReboundStockStatus(stock) {
      if (!stock) return { status: 'untriggered', text: '备选观察', badgeClass: 'badge-untriggered', tooltip: '' }
      
      // 1. 检查是否在反弹账户已成交买入
      const buyTime = this.reboundBuys?.[stock.code] || stock.buy_time
      const reboundTodayBuys = this.todayBuys?.rebound || []
      const isBoughtToday = buyTime || stock.bought || reboundTodayBuys.some(item => (item.code || '').startsWith(stock.code))
      if (isBoughtToday) {
        const timeStr = buyTime ? `(${buyTime})` : ''
        return {
          status: 'bought',
          text: `买入${timeStr}`,
          badgeClass: 'badge-buy',
          tooltip: `反弹策略已执行买入成交${buyTime ? `（${buyTime}）` : ''}`
        }
      }

      // 2. 检查持仓
      const isHolding = this.reboundHoldingCodes?.includes(stock.code) || stock.holding
      if (isHolding) {
        return {
          status: 'holding',
          text: '已持仓',
          badgeClass: 'badge-avoid',
          tooltip: '反弹策略当前已持仓该标的'
        }
      }

      // 3. 检查涨停
      if (stock.is_limit_up) {
        return {
          status: 'limit_up',
          text: '涨停',
          badgeClass: 'badge-danger',
          tooltip: '标的已封涨停，暂停反弹追入'
        }
      }

      // 4. 实时反弹买入触发区间判断
      const cfg = this.reboundOptimalConfig || {
        gem_buy_min: 5.0, gem_buy_max: 11.0,
        main_buy_min: 3.0, main_buy_max: 6.5
      }
      const isGem = stock.code.startsWith('30') || stock.code.startsWith('68')
      const minPct = isGem ? (cfg.gem_buy_min ?? 5.0) : (cfg.main_buy_min ?? 3.0)
      const maxPct = isGem ? (cfg.gem_buy_max ?? 11.0) : (cfg.main_buy_max ?? 6.5)
      const boardName = isGem ? '双创' : '主板'
      const pct = Number(stock.pct_chg)

      if (Number.isFinite(pct)) {
        if (pct >= minPct && pct <= maxPct) {
          return {
            status: 'triggered',
            text: '区间达标',
            badgeClass: 'badge-buy',
            tooltip: `${boardName}涨幅区间 [${minPct}% ~ ${maxPct}%]，当前 ${pct.toFixed(2)}%；自动买入还需在10:30前满足量能、有效行情、可用仓位及现金条件，以成交记录为准。`
          }
        } else if (pct > maxPct) {
          return {
            status: 'over',
            text: '超买点',
            badgeClass: 'badge-v3-silent',
            tooltip: `当前涨幅 ${pct.toFixed(2)}% 已超过${boardName}买入上限 (${maxPct}%)`
          }
        } else {
          const diff = (minPct - pct).toFixed(2)
          return {
            status: 'untriggered',
            text: '待触发',
            badgeClass: 'badge-untriggered',
            tooltip: `${boardName}买入区间 [${minPct}% ~ ${maxPct}%]，当前 ${pct.toFixed(2)}%（距触发差 ${diff}%）`
          }
        }
      }

      return {
        status: 'untriggered',
        text: '备选观察',
        badgeClass: 'badge-untriggered',
        tooltip: `距最高点 ${stock.peak_age_days ?? '—'} 天，波段涨幅 ${this.formatReboundPct(stock.prior_gain)}`
      }
    },
    openReboundRealBuy(stock) {
      if (!stock) return
      this.realBuyForm = {
        code: stock.code,
        name: stock.name || stock.code,
        price: stock.price || null,
        strategy: 'rebound',
        reboundGroup: 'v2',
        positionPct: this.getDefaultRealPositionPct('rebound', 0),
        targetIdx: 0,
        sendNotification: false,
        blockExistingPosition: true
      }
      this.showRealBuyModal = true
    },
    formatReboundGeneTags(gene) {
      if (!gene) return []
      const cleaned = String(gene).replace(/^前期强势标的[：:]\s*/, '').trim()
      if (!cleaned) return []
      return cleaned.split(/[、,，]/).map(s => s.trim()).filter(Boolean)
    },
    checkTheme() {
      const siteTheme = typeof localStorage !== 'undefined' ? localStorage.getItem('site.theme') : null
      const bodyDark = typeof document !== 'undefined' && document.body.classList.contains('theme-dark')
      this.isDarkMode = siteTheme ? siteTheme === 'dark' : bodyDark
    },
    async refreshPage() {
      this.refreshing = true
      try {
        await this.initializePage()
      } finally {
        this.refreshing = false
      }
    },
    async initializePage() {
      const requestedDate = this.selectedDate
      this.v2AbGroup = null
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama/config',
          method: 'get',
          params: { date: requestedDate }
        })
        if (this.selectedDate !== requestedDate) return
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          const config = res.data.data || {}
          this.v2AbGroup = config.v2_ab_group || null
          this.supplementH67 = Boolean(config.supplement_h67)
          this.filterTopicNet5 = config.filter_topic_net5 !== false
          this.v2PositionPcts = (config.v2_position_fractions || [0.25, 0.25]).map(value => Math.round(value * 1000) / 10)
          this.v3PositionPcts = (config.v3_position_fractions || [0.15, 0.15, 0.08, 0.07, 0.05]).map(value => Math.round(value * 1000) / 10)
          this.v2RealPositionPcts = (config.v2_real_position_fractions || config.v2_position_fractions || [0.25, 0.25]).map(value => Math.round(value * 1000) / 10)
          this.v3RealPositionPcts = (config.v3_real_position_fractions || config.v3_position_fractions || [0.15, 0.15, 0.08, 0.07, 0.05]).map(value => Math.round(value * 1000) / 10)
          this.v2MarketMode = config.v2_market_mode || 'main'
          this.v2ActiveMode = config.active_mode || this.v2MarketMode
          this.reboundAccountId = config.rebound_account_id || 5
          this.reboundPositionPcts = (config.rebound_position_fractions || [0.4, 0.4]).map(value => value * 100)
          this.reboundRealPositionPcts = (config.rebound_real_position_fractions || [0.4, 0.4]).map(value => value * 100)
          const reboundGroups = config.rebound_real_groups || { v2: { enabled: config.v2_market_mode === 'rebound', fractions: config.rebound_real_position_fractions || [0.4, 0.4] }, v3: { enabled: false, fractions: [0.4, 0.4] } }
          for (const group of ['v2', 'v3']) this.$set(this.reboundRealGroups, group, { enabled: reboundGroups[group].enabled, pcts: reboundGroups[group].fractions.map(value => value * 100) })
          this.switchingReason = config.switching_reason || ''
          this.reboundCandidates = config.rebound_candidates || []
          this.reboundBuys = config.rebound_buys || {}
          this.reboundHoldingCodes = config.rebound_holding_codes || []
          this.crowding = config.crowding || { available: false, level: 'unknown' }
        }
      } catch (e) {
        console.error('读取全景配置失败，使用默认值', e)
      }
      await this.fetchTodayBuys()
      await this.fetchData()
      if (this.autoSync) {
        if (this.isCurrentDateToday) {
          this.initWebSocket()
        } else {
          this.closeWebSocket()
        }
      }
    },
    async changeV2MarketMode(mode) {
      if (!['main', 'rebound'].includes(mode) || !this.isCurrentDateToday || mode === this.v2MarketMode) return
      this.v2MarketMode = mode
      this.savingConfig = true
      try { await this.updateMonitorConfig() } finally { this.savingConfig = false }
    },
    async onReboundRealGroupToggle(checked, group) {
      if (!this.isCurrentDateToday || this.savingConfig) return
      this.reboundRealGroups[group].enabled = Boolean(checked)
      await this.savePositionConfig()
    },
    async fetchTodayBuys() {
      try {
        const requests = [
          request_zzquant({ url: '/v3/quant/strategy/daily-details', method: 'get', params: { date: this.selectedDate, account_id: 3 }, noRedirect: true }),
          request_zzquant({ url: '/v3/quant/strategy/daily-details', method: 'get', params: { date: this.selectedDate, account_id: 4 }, noRedirect: true }),
          request_zzquant({ url: '/v3/quant/strategy/daily-details', method: 'get', params: { date: this.selectedDate, account_id: this.reboundAccountId }, noRedirect: true })
        ]
        const [v2Res, v3Res, reboundRes] = await Promise.all(requests)
        this.todayBuys = {
          v2: this.extractBuyTargets(v2Res),
          v3: this.extractBuyTargets(v3Res),
          rebound: this.extractBuyTargets(reboundRes)
        }
      } catch (e) {
        console.error('读取当日买入标的失败', e)
        this.todayBuys = { v2: [], v3: [] }
      }
    },
    extractBuyTargets(response) {
      const records = response?.data?.data?.records || []
      const seen = new Set()
      return records
        .filter(record => record.trade_type === '买入')
        .filter(record => {
          const key = record.symbol_code || record.symbol_name
          if (!key || seen.has(key)) return false
          seen.add(key)
          return true
        })
        .map(record => ({ code: record.symbol_code, name: record.symbol_name || record.symbol_code }))
    },
    todayBuyText(version) {
      const targets = this.todayBuys[version] || []
      return targets.length ? targets.map(item => item.name).join('、') : '暂无'
    },
    async updateMonitorConfig(refreshAfter = true) {
      if (this.selectedDate !== this.todayStr) {
        await this.fetchData()
        return
      }
      this.loading = true
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama/config',
          method: 'post',
          data: {
            supplement_h67: this.supplementH67,
            filter_topic_net5: this.filterTopicNet5,
            v2_position_fractions: this.v2PositionPcts.map(value => Number(value) / 100),
            v3_position_fractions: this.v3PositionPcts.map(value => Number(value) / 100),
            v2_real_position_fractions: this.v2RealPositionPcts.map(value => Number(value) / 100),
            v3_real_position_fractions: this.v3RealPositionPcts.map(value => Number(value) / 100),
            rebound_position_fractions: this.reboundPositionPcts.map(value => Number(value) / 100),
            rebound_real_position_fractions: this.reboundRealPositionPcts.map(value => Number(value) / 100),
            rebound_real_groups: Object.fromEntries(['v2', 'v3'].map(group => [group, { enabled: this.reboundRealGroups[group].enabled, fractions: this.reboundRealGroups[group].pcts.map(value => Number(value) / 100) }])),
            v2_market_mode: this.v2MarketMode
          }
        })
        if (!res.data || (res.data.code !== 200 && res.data.code !== 20000)) {
          throw new Error(res.data?.msg || res.data?.message || '保存配置失败')
        }
        const cfg = res.data.data || {}
        this.crowding = cfg.crowding || this.crowding
        this.v2MarketMode = cfg.v2_market_mode || this.v2MarketMode
        this.v2ActiveMode = cfg.active_mode || this.v2ActiveMode
        this.switchingReason = cfg.switching_reason || this.switchingReason
        this.reboundCandidates = cfg.rebound_candidates || this.reboundCandidates
        this.$bvToast.toast('配置已保存，实盘将在下一监控周期刷新候选池', {
          title: '实盘配置',
          variant: 'success',
          solid: true
        })
      } catch (e) {
        console.error(e)
        this.$bvToast.toast(e.response?.data?.detail || e.message || '保存配置失败', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
        await this.initializePage()
        return
      }
      if (refreshAfter) await this.fetchData()
    },
    positionTotal(values) {
      return values.reduce((total, value) => total + (Number(value) || 0), 0).toFixed(1)
    },
    recommendedPositionText(version) {
      const recommendation = this.crowding.recommendations?.[version]
      if (!recommendation) return '暂无'
      if (recommendation.total_position === 0) return '0%（禁止新开仓）'
      const details = recommendation.position_fractions.map(value => `${(value * 100).toFixed(1)}%`).join('/')
      const realDetails = recommendation.real_position_fractions ? recommendation.real_position_fractions.map(value => `${(value * 100).toFixed(1)}%`).join('/') : details
      return `模拟 ${(recommendation.total_position * 100).toFixed(1)}%（${details}） ｜ 实盘 ${(recommendation.real_total_position * 100).toFixed(1)}%（${realDetails}）`
    },
    async savePositionConfig() {
      const allValues = [
        ...this.v2PositionPcts,
        ...this.v3PositionPcts,
        ...this.v2RealPositionPcts,
        ...this.v3RealPositionPcts,
        ...this.reboundPositionPcts,
        ...this.reboundRealPositionPcts,
        ...this.reboundRealGroups.v2.pcts,
        ...this.reboundRealGroups.v3.pcts
      ]
      const invalid = allValues.some(value => !Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 100)
      if (
        invalid ||
        Number(this.positionTotal(this.v2PositionPcts)) > 100 ||
        Number(this.positionTotal(this.v3PositionPcts)) > 100 ||
        Number(this.positionTotal(this.v2RealPositionPcts)) > 100 ||
        Number(this.positionTotal(this.v3RealPositionPcts)) > 100 ||
        Number(this.positionTotal(this.reboundPositionPcts)) > 100 ||
        Number(this.positionTotal(this.reboundRealPositionPcts)) > 100 ||
        Number(this.positionTotal(this.reboundRealGroups.v2.pcts)) > 100 ||
        Number(this.positionTotal(this.reboundRealGroups.v3.pcts)) > 100
      ) {
        this.$bvToast.toast('每笔仓位需在0%至100%之间（0为不开仓），单策略总仓位不能超过100%', { title: '仓位配置有误', variant: 'danger', solid: true })
        return
      }
      this.savingConfig = true
      try {
        await this.updateMonitorConfig(false)
      } finally {
        this.savingConfig = false
      }
    },
    toggleExpand(topicName) {
      this.$set(this.expandedTopics, topicName, !this.expandedTopics[topicName])
    },
    getDisplayCandidates(candidates, topicName) {
      if (!candidates) return []
      let list = candidates.slice()
      if (this.mainSortKey && this.mainSortOrder) {
        const key = this.mainSortKey
        const isAsc = this.mainSortOrder === 'asc'
        list.sort((a, b) => {
          let cmp = 0
          if (key === 'name') {
            const na = a.name || a.code6 || ''
            const nb = b.name || b.code6 || ''
            cmp = na.localeCompare(nb, 'zh-CN')
          } else if (key === 'price') {
            const pa = Number(a.price) || 0
            const pb = Number(b.price) || 0
            cmp = pa - pb
          } else if (key === 'amount') {
            const aa = Number(a.amount) || 0
            const ab = Number(b.amount) || 0
            cmp = aa - ab
          } else if (key === 'pct_change') {
            const ga = Number(a.pct_change) || 0
            const gb = Number(b.pct_change) || 0
            cmp = ga - gb
          }
          return isAsc ? cmp : -cmp
        })
      }
      const isExpanded = this.expandedTopics[topicName]
      if (isExpanded || list.length <= 10) {
        return list
      }
      return list.slice(0, 10)
    },
    toggleReboundSort(key) {
      if (this.reboundSortKey === key) {
        if (this.reboundSortOrder === 'desc') {
          this.reboundSortOrder = 'asc'
        } else if (this.reboundSortOrder === 'asc') {
          this.reboundSortKey = null
          this.reboundSortOrder = null
        }
      } else {
        this.reboundSortKey = key
        if (key === 'code' || key === 'peak_age_days') {
          this.reboundSortOrder = 'asc'
        } else {
          this.reboundSortOrder = 'desc'
        }
      }
    },
    resetReboundSort() {
      this.reboundSortKey = null
      this.reboundSortOrder = null
    },
    getReboundSortTitle(key, label) {
      if (this.reboundSortKey === key) {
        const current = this.reboundSortOrder === 'asc' ? '升序 ↑' : '降序 ↓'
        const next = this.reboundSortOrder === 'desc' ? '升序 ↑' : '恢复默认'
        return `当前已按 ${label} ${current}，点击切换为 ${next}`
      }
      return `点击按 ${label} 排序`
    },
    toggleReboundTopicSort(key) {
      if (this.reboundTopicSortKey === key) {
        if (this.reboundTopicSortOrder === 'desc') {
          this.reboundTopicSortOrder = 'asc'
        } else if (this.reboundTopicSortOrder === 'asc') {
          this.reboundTopicSortKey = null
          this.reboundTopicSortOrder = null
        }
      } else {
        this.reboundTopicSortKey = key
        if (key === 'topic_name') {
          this.reboundTopicSortOrder = 'asc'
        } else {
          this.reboundTopicSortOrder = 'desc'
        }
      }
    },
    resetReboundTopicSort() {
      this.reboundTopicSortKey = null
      this.reboundTopicSortOrder = null
    },
    getReboundTopicSortTitle(key, label) {
      if (this.reboundTopicSortKey === key) {
        const current = this.reboundTopicSortOrder === 'asc' ? '升序 ↑' : '降序 ↓'
        const next = this.reboundTopicSortOrder === 'desc' ? '升序 ↑' : '恢复默认'
        return `当前已按 ${label} ${current}，点击切换为 ${next}`
      }
      return `点击按 ${label} 排序`
    },
    toggleMainCandidatesSort(key) {
      if (this.mainSortKey === key) {
        if (this.mainSortOrder === 'desc') {
          this.mainSortOrder = 'asc'
        } else if (this.mainSortOrder === 'asc') {
          this.mainSortKey = null
          this.mainSortOrder = null
        }
      } else {
        this.mainSortKey = key
        if (key === 'name') {
          this.mainSortOrder = 'asc'
        } else {
          this.mainSortOrder = 'desc'
        }
      }
    },
    resetMainCandidatesSort() {
      this.mainSortKey = null
      this.mainSortOrder = null
    },
    getMainCandidatesSortTitle(key, label) {
      if (this.mainSortKey === key) {
        const current = this.mainSortOrder === 'asc' ? '升序 ↑' : '降序 ↓'
        const next = this.mainSortOrder === 'desc' ? '升序 ↑' : '恢复默认'
        return `当前已按 ${label} ${current}，点击切换为 ${next}`
      }
      return `点击按 ${label} 排序`
    },
    showStockInfo(code, name) {
      this.sidebarSymbolCode = code || ''
      this.sidebarSymbolName = name || ''
      this.$nextTick(() => {
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
      })
    },
    resetToToday() {
      this.selectedDate = this.todayStr
      this.initializePage()
    },
    async fetchReboundQuotes() {
      const date = this.selectedDate
      if (!this.isCurrentDateToday) {
        this.reboundQuotes = {}
        return
      }
      const codes = [...new Set(this.currentReboundCandidates.flatMap(topic => this.getReboundStocks(topic).map(stock => stock.code)))]
      if (!codes.length) {
        this.reboundQuotes = {}
        return
      }
      try {
        const nextQuotes = {}
        for (let index = 0; index < codes.length; index += 300) {
          const res = await request_zzquant({
            url: '/v3/topic/monitor/panorama/rebound-quotes', method: 'get',
            params: { codes: codes.slice(index, index + 300).join(',') }
          })
          if (res.data && (res.data.code === 200 || res.data.code === 20000)) Object.assign(nextQuotes, res.data.data || {})
        }
        if (this.selectedDate === date) this.reboundQuotes = nextQuotes
      } catch (error) {
        if (this.selectedDate === date) this.reboundQuotes = {}
        console.error('反弹实时行情获取失败:', error)
      }
    },
    async fetchData(silent = false) {
      if (!silent) {
        this.loading = true
      }
      try {
        const params = {
          supplement_h67: this.supplementH67,
          filter_topic_net5: this.filterTopicNet5
        }
        if (this.selectedDate) {
          params.date = this.selectedDate
        }
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama',
          method: 'get',
          params
        })
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          this.topics = res.data.data || []
          try {
            this.syncTopicChecksFromBackend()
          } catch (err) {
            console.warn('同步题材勾选状态异常:', err)
          }
          try {
            await this.fetchReboundQuotes()
          } catch (err) {
            console.warn('获取反弹行情异常:', err)
          }
          if (silent) {
            const now = new Date()
            const h = String(now.getHours()).padStart(2, '0')
            const m = String(now.getMinutes()).padStart(2, '0')
            const s = String(now.getSeconds()).padStart(2, '0')
            this.lastSyncTime = `${h}:${m}:${s}`
          }
        } else if (!silent) {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '获取数据失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error('全景数据获取失败:', e)
        if (!silent) {
          const errMsg = e.response?.data?.msg || e.response?.data?.message || e.response?.data?.detail || e.message || '网络请求异常'
          this.$bvToast.toast(errMsg, {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } finally {
        if (!silent) {
          this.loading = false
        }
      }
    },
    toggleAutoSync() {
      localStorage.setItem('panorama_auto_sync', this.autoSync ? 'true' : 'false')
      if (this.autoSync && this.isCurrentDateToday) {
        this.initWebSocket()
        this.performSilentSync()
      } else {
        this.closeWebSocket()
      }
    },
    handleVisibilityChange() {
      if (!document.hidden && this.pendingSyncOnVisible && this.autoSync && this.isCurrentDateToday) {
        this.pendingSyncOnVisible = false
        this.performSilentSync()
      }
    },
    async performSilentSync() {
      if (this.silentSyncing || !this.isCurrentDateToday) return
      this.silentSyncing = true
      try {
        await Promise.all([
          this.fetchData(true),
          this.fetchTodayBuys()
        ])
      } catch (e) {
        console.error('静默自动同步异常:', e)
      } finally {
        this.silentSyncing = false
      }
    },
    initWebSocket() {
      if (!this.autoSync || !this.isCurrentDateToday || this.ws) return

      const token = localStorage.getItem('token')
      if (!token) {
        this.$bvToast.toast('请先登录', { variant: 'danger' })
        this.autoSync = false
        return
      }

      const wsUrl = `wss://api.zizizaizai.com/v3/ws/topic_monitor?token=${encodeURIComponent(token)}`
      try {
        this.ws = new WebSocket(wsUrl)
        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            if (data.type === 'panorama_tick' || data.type === 'topic_alert' || data.type === 'topic_check_updated') {
              if (this.autoSync && this.isCurrentDateToday) {
                if (document.hidden) {
                  this.pendingSyncOnVisible = true
                } else {
                  this.performSilentSync()
                }
              }
            }
          } catch (e) {
            console.error('Error parsing WS message in Panorama:', e)
          }
        }
        this.ws.onclose = () => {
          this.ws = null
          if (this.autoSync && this.isCurrentDateToday) {
            this.wsReconnectTimer = setTimeout(() => this.initWebSocket(), 5000)
          }
        }
        this.ws.onerror = (err) => {
          console.error('Panorama WS error:', err)
        }
      } catch (err) {
        console.error('Failed to initialize Panorama WS:', err)
      }
    },
    closeWebSocket() {
      if (this.wsReconnectTimer) {
        clearTimeout(this.wsReconnectTimer)
        this.wsReconnectTimer = null
      }
      if (this.ws) {
        try {
          this.ws.onopen = null
          this.ws.onmessage = null
          this.ws.onclose = null
          this.ws.onerror = null
          this.ws.close()
        } catch (e) {
          // ignore
        }
        this.ws = null
      }
    },
    getPnlClass(val) {
      if (val > 0) return 'text-danger'
      if (val < 0) return 'text-success'
      return 'text-muted'
    },
    formatTopicNet5(topic) {
      if (!topic) return ''
      const net = topic.net5 !== undefined && topic.net5 !== null ? topic.net5 : topic.NET5
      if (net === undefined || net === null) return ''
      const sign = net > 0 ? '+' : ''
      if (topic.limit_up5 !== undefined && topic.limit_up5 !== null && topic.limit_down5 !== undefined && topic.limit_down5 !== null) {
        return `${sign}${net}(${topic.limit_up5}/${topic.limit_down5})`
      }
      return `${sign}${net}`
    },
    getNet5BadgeClass(topic) {
      if (!topic) return ''
      const net = topic.net5 !== undefined && topic.net5 !== null ? topic.net5 : topic.NET5
      if (net === undefined || net === null) return ''
      if (net > 0) return 'badge-net-positive'
      if (net < 0) return 'badge-net-negative'
      return 'badge-net-zero'
    },
    showTopicKline(topic) {
      const key = (topic && (topic.unique_key || topic.topic_id)) ? (topic.unique_key || String(topic.topic_id)) : null
      if (key) {
        this.selectedTopicUniqueKey = key
        this.selectedTopicName = (topic && topic.topic_name) || ''
        this.showTopicKlineModal = true
      }
    },
    getDefaultRealPositionPct(strategy, targetIdx = 0) {
      const pcts = strategy === 'rebound' ? this.reboundRealGroups[this.realBuyForm.reboundGroup || 'v2'].pcts : (strategy === 'v3' ? this.v3RealPositionPcts : this.v2RealPositionPcts)
      if (Array.isArray(pcts) && pcts.length > 0) {
        if (targetIdx >= 0 && targetIdx < pcts.length && Number(pcts[targetIdx]) > 0) {
          return Number(pcts[targetIdx])
        }
        const firstNonZero = pcts.find(p => Number(p) > 0)
        if (firstNonZero !== undefined) {
          return Number(firstNonZero)
        }
        return Number(pcts[0]) || 5.0
      }
      return 5.0
    },
    openRealBuyModal(item, version = 'v2', idx = 0) {
      const defaultStrategy = version === 'v3' ? 'v3' : 'v2'
      const defaultPct = this.getDefaultRealPositionPct(defaultStrategy, idx)
      this.realBuyForm = {
        code: item.code,
        name: item.name || item.code,
        price: item.price || null,
        strategy: defaultStrategy,
        positionPct: defaultPct,
        targetIdx: idx,
        sendNotification: false,
        blockExistingPosition: true
      }
      this.showRealBuyModal = true
    },
    onRealBuyStrategyChange(strategy) {
      this.realBuyForm.strategy = strategy
      this.realBuyForm.positionPct = this.getDefaultRealPositionPct(strategy, this.realBuyForm.targetIdx)
    },
    async checkRealBuyPosition() {
      const requestId = ++this.realBuyPositionRequest
      this.realBuyPosition = null
      this.realBuyPositionError = false
      this.realBuyPositionLoading = true
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/real_buy_position',
          method: 'get',
          params: {
            code: String(this.realBuyForm.code).slice(0, 6),
            strategy: this.realBuyForm.strategy,
            rebound_group: this.realBuyForm.reboundGroup || 'v2'
          }
        })
        if (requestId !== this.realBuyPositionRequest) return
        if (![200, 20000].includes(res.data?.code) || typeof res.data?.data?.held !== 'boolean') throw new Error('持仓查询失败')
        this.realBuyPosition = res.data.data
      } catch (e) {
        if (requestId === this.realBuyPositionRequest) this.realBuyPositionError = true
      } finally {
        if (requestId === this.realBuyPositionRequest) this.realBuyPositionLoading = false
      }
    },
    async submitRealBuy() {
      if (this.submittingRealBuy || this.realBuyPositionBlocked) return
      const pct = Number(this.realBuyForm.positionPct)
      if (!pct || pct <= 0 || pct > 100) {
        this.$bvToast.toast('请输入有效的买入仓位比例（0.1% ~ 100%）', {
          title: '提示',
          variant: 'warning',
          solid: true
        })
        return
      }
      if (!this.realBuyForm.code) {
        this.$bvToast.toast('标的代码缺失', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
        return
      }
      this.submittingRealBuy = true
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/trigger_real_buy',
          method: 'post',
          data: {
            code: this.realBuyForm.code,
            name: this.realBuyForm.name,
            strategy: this.realBuyForm.strategy,
            rebound_group: this.realBuyForm.reboundGroup || 'v2',
            position_percent: pct / 100,
            send_notification: Boolean(this.realBuyForm.sendNotification),
            block_existing_position: this.realBuyForm.blockExistingPosition !== false
          }
        })
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          this.$bvToast.toast(res.data.message || `标的 ${this.realBuyForm.name} 实盘点火买入成功！`, {
            title: '实盘买入成功',
            variant: 'success',
            solid: true
          })
          this.showRealBuyModal = false
          this.fetchTodayBuys()
        } else {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '实盘买入点火失败', {
            title: '点火失败',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error('实盘点火买入异常', e)
        const errMsg = e.response?.data?.detail || e.detail || e.message || '网络请求异常'
        this.$bvToast.toast(errMsg, {
          title: '点火异常',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.submittingRealBuy = false
      }
    },
    confirmManualTrigger(code6, name) {
      this.openRealBuyModal({ code: code6, name: name }, 'v2', 0)
    },
    async triggerManual(code6) {
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/trigger_manual',
          method: 'post',
          data: {
            code: code6
          }
        })
        if (res.data && res.data.code === 200) {
          this.$bvToast.toast(`个股 ${code6} 手动点火成功！`, {
            title: '成功',
            variant: 'success',
            solid: true
          })
          this.fetchData()
        } else {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '点火失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error(e)
        const errMsg = e.response?.data?.detail || '网络请求异常'
        this.$bvToast.toast(errMsg, {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      }
    },
    shortenStatus(text) {
      if (!text || text === '-') return '-'
      let t = String(text).trim()
      t = t.replace('below_MA20', '破MA20')
      t = t.replace('no_new_high', '非新高')
      t = t.replace('非近30日新高', '非新高')
      t = t.replace('上市不足1年', '次新')
      t = t.replace('次新股', '次新')
      t = t.replace('昨日涨幅过大', '昨大涨')
      t = t.replace('未触发(已达)', '已达标')

      t = t.replace('避(偏离超买)', '避超买')
      t = t.replace('避(市值<54亿)', '避市值')
      t = t.replace('避(题材排除)', '避题材')
      t = t.replace('避(已持仓)', '避持仓')
      t = t.replace('避(今日卖出)', '避今卖')
      t = t.replace('避(低开<-8%)', '避低开')
      t = t.replace('避(昨日涨幅过大)', '避昨涨')
      t = t.replace('避雷针', '避雷')

      if (t.startsWith('买入(')) {
        t = '买' + t.slice(3, -1)
      } else if (t === '买入') {
        t = '买'
      } else if (t.startsWith('静默(')) {
        t = '静' + t.slice(3, -1)
      } else if (t === '静默') {
        t = '静'
      } else if (t.startsWith('触发(')) {
        t = '触' + t.slice(3, -1)
      } else if (t === '触发') {
        t = '触'
      } else if (t.startsWith('占位(')) {
        t = '占' + t.slice(3, -1)
      } else if (t === '占位') {
        t = '占'
      }
      return t
    },
    getRawV2Text(stock) {
      if (stock && stock.v2 && stock.v2.text) return stock.v2.text
      if (stock && stock.triggered) return `触发${stock.trigger_time ? `(${stock.trigger_time})` : ''}`
      if (stock && stock.filter_reason === '占位') return `占位${stock.trigger_time ? `(${stock.trigger_time})` : ''}`
      if (stock && stock.filter_reason) return stock.filter_reason
      if (stock && stock.is_met_buy_condition) return '未触发(已达)'
      return '未触发'
    },
    getV2BadgeText(stock) {
      return this.shortenStatus(this.getRawV2Text(stock))
    },
    getRawV3Text(stock) {
      if (stock && stock.v3 && stock.v3.text) return stock.v3.text
      return '-'
    },
    getV3BadgeText(stock) {
      return this.shortenStatus(this.getRawV3Text(stock))
    },
    isStatusMerged(stock) {
      const v2 = this.getV2BadgeText(stock)
      const v3 = this.getV3BadgeText(stock)
      if (v3 === '-') return true
      return v2 === v3
    },
    getMergedBadgeText(stock) {
      return this.getV2BadgeText(stock)
    },
    getMergedBadgeClass(stock) {
      return this.getV2BadgeClass(stock)
    },
    getStatusTooltip(stock) {
      const v2 = this.getRawV2Text(stock)
      const v3 = this.getRawV3Text(stock)
      return `V2: ${v2} | V3: ${v3}`
    },
    formatAmountMkv(amount, mkv) {
      const a = (amount || 0) / 100000000
      const m = (mkv || 0) / 100000000
      const aStr = a >= 10 ? a.toFixed(0) : a.toFixed(1)
      const mStr = m >= 10 ? m.toFixed(0) : m.toFixed(1)
      return `${aStr}/${mStr}`
    },
    getV2BadgeClass(stock) {
      const type = stock?.v2?.badge_type || stock?.v2?.status
      if (type === 'buy' || stock?.triggered) return 'badge-buy'
      if (type === 'placeholder' || stock?.filter_reason === '占位') return 'badge-placeholder'
      if (type === 'avoid' || (stock?.filter_reason && (stock.filter_reason.indexOf('避') === 0 || stock.filter_reason.indexOf('避雷') > -1))) return 'badge-avoid'
      if (type === 'muted' || (stock?.filter_reason && !stock.triggered)) return 'badge-muted-tag'
      if (type === 'met' || stock?.is_met_buy_condition) return 'badge-met-condition'
      return 'badge-untriggered'
    },
    getV3BadgeClass(stock) {
      const type = stock?.v3?.badge_type || stock?.v3?.status
      if (type === 'buy' || type === 'triggered') return 'badge-buy'
      if (type === 'silent') return 'badge-v3-silent'
      if (type === 'avoid') return 'badge-avoid'
      if (type === 'muted') return 'badge-muted-tag'
      if (type === 'met') return 'badge-met-condition'
      return 'badge-untriggered'
    },
    isCanManualTrigger(stock) {
      if (!stock) return false
      if (stock.triggered) return false
      if (stock.v2 && (stock.v2.bought || stock.v2.status === 'triggered' || stock.v2.status === 'buy')) {
        return false
      }
      return true
    },
    getScoreClass(score) {
      if (score >= 3) return 'score-high'
      if (score >= 1) return 'score-mid'
      return 'score-low'
    },
    getScoreTooltip(stock) {
      if (!stock || stock.score === undefined || stock.score === null) return ''
      const details = stock.score_details || {}

      let timing = details.timing !== undefined ? details.timing : null
      let ma5 = details.ma5 !== undefined ? details.ma5 : (details.dist_ma5 !== undefined ? details.dist_ma5 : null)
      let window = details.window !== undefined ? details.window : (details.time !== undefined ? details.time : null)
      let upRatio = details.up_ratio !== undefined ? details.up_ratio : (details.breadth !== undefined ? details.breadth : null)
      let avgPct = details.avg_pct !== undefined ? details.avg_pct : (details.theme_pct !== undefined ? details.theme_pct : null)

      // 若 details 字段缺失，从 score_str (格式如 '+2 (0+0+1+1+0)') 兜底解析 [up_ratio, avg_pct, timing, window, ma5]
      if (timing === null || ma5 === null || window === null || upRatio === null || avgPct === null) {
        if (stock.score_str && stock.score_str.indexOf('(') > -1) {
          const m = stock.score_str.match(/\(([^)]+)\)/)
          if (m && m[1]) {
            const nums = m[1].match(/[+-]?\d+/g)
            if (nums && nums.length >= 5) {
              if (upRatio === null) upRatio = parseInt(nums[0], 10) || 0
              if (avgPct === null) avgPct = parseInt(nums[1], 10) || 0
              if (timing === null) timing = parseInt(nums[2], 10) || 0
              if (window === null) window = parseInt(nums[3], 10) || 0
              if (ma5 === null) ma5 = parseInt(nums[4], 10) || 0
            }
          }
        }
      }

      timing = timing ?? 0
      ma5 = ma5 ?? 0
      window = window ?? 0
      upRatio = upRatio ?? 0
      avgPct = avgPct ?? 0

      const fmt = (label, val) => `${label}:${val > 0 ? '+' : ''}${val}`
      const dStr = [
        fmt('大盘', timing),
        fmt('均线', ma5),
        fmt('时效', window),
        fmt('广度', upRatio),
        fmt('题材', avgPct)
      ].join(' | ')

      const raw = details.raw || {}
      const parts = []
      if (raw.dist_ma5 !== undefined && raw.dist_ma5 !== null) parts.push(`乖离MA5:${raw.dist_ma5}%`)
      if (raw.trigger_time) parts.push(`时点:${raw.trigger_time}`)
      if (raw.up_count !== undefined && raw.down_count !== undefined) parts.push(`板块涨跌:${raw.up_count}/${raw.down_count}`)
      if (raw.avg_pct !== undefined && raw.avg_pct !== null) parts.push(`板块均幅:${raw.avg_pct}%`)

      const rawSuffix = parts.length > 0 ? `\n(${parts.join(', ')})` : ''
      return `确定性评分: ${stock.score > 0 ? '+' : ''}${stock.score} (${dStr})${rawSuffix}`
    },
    getCleanTopicName(name) {
      return String(name || '').replace(/[(（].*?[)）]/g, '').trim()
    },
    isTopicChecked(topic) {
      if (!topic) return true
      const cleanName = this.getCleanTopicName(topic.topic_name)
      const nowSec = this.currentTimeSec || (Date.now() / 1000)
      const local = this.topicCheckMap[cleanName]
      if (local !== undefined) {
        if (local.checked === false) {
          if (local.expireAt && local.expireAt <= nowSec) {
            return true
          }
          return false
        }
        return true
      }
      if (topic.user_checked === false) {
        if (topic.user_expire_at && topic.user_expire_at <= nowSec) {
          return true
        }
        return false
      }
      return true
    },
    getTopicRemainingSeconds(topic) {
      if (!topic) return 0
      const cleanName = this.getCleanTopicName(topic.topic_name)
      const nowSec = this.currentTimeSec || (Date.now() / 1000)
      const local = this.topicCheckMap[cleanName]
      if (local && local.checked === false && local.expireAt > nowSec) {
        return Math.max(0, Math.round(local.expireAt - nowSec))
      }
      if (topic.user_checked === false && topic.user_expire_at > nowSec) {
        return Math.max(0, Math.round(topic.user_expire_at - nowSec))
      }
      return 0
    },
    getTopicRemainingHoursText(topic) {
      const sec = this.getTopicRemainingSeconds(topic)
      if (sec <= 0) return ''
      if (sec >= 3600) {
        return (sec / 3600).toFixed(1) + 'h'
      }
      return Math.max(1, Math.ceil(sec / 60)) + 'm'
    },
    async handleTopicCheckToggle(topic, checked) {
      const cleanName = this.getCleanTopicName(topic.topic_name)
      const isChecked = Boolean(checked)
      const nowSec = Date.now() / 1000
      const expireAt = isChecked ? 0 : (nowSec + 12 * 3600)
      const remainingSeconds = isChecked ? 0 : 12 * 3600

      this.$set(this.topicCheckMap, cleanName, {
        checked: isChecked,
        expireAt,
        remainingSeconds
      })
      this.$set(topic, 'user_checked', isChecked)
      this.$set(topic, 'user_expire_at', isChecked ? null : expireAt)
      this.$set(topic, 'user_remaining_seconds', remainingSeconds)
      this.saveTopicExclusionsToLocal()

      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/topic-check-status',
          method: 'post',
          data: {
            topic_name: cleanName,
            checked: isChecked,
            duration_hours: 12.0
          }
        })
        if (!res.data || (res.data.code !== 200 && res.data.code !== 20000)) {
          throw new Error(res.data?.msg || res.data?.message || '操作失败')
        }
        this.$bvToast.toast(
          isChecked ? `已开启【${cleanName}】作为当日备选题材` : `已排除【${cleanName}】（12小时内不作备选）`,
          {
            title: '备选题材设置',
            variant: isChecked ? 'success' : 'secondary',
            solid: true,
            autoHideDelay: 2500
          }
        )
        await this.fetchData(true)
      } catch (e) {
        console.error('更新题材勾选状态失败', e)
        const prevChecked = !isChecked
        this.$set(topic, 'user_checked', prevChecked)
        this.$set(this.topicCheckMap, cleanName, {
          checked: prevChecked,
          expireAt: prevChecked ? 0 : (Date.now() / 1000 + 12 * 3600),
          remainingSeconds: prevChecked ? 0 : 12 * 3600
        })
        this.saveTopicExclusionsToLocal()
        this.$bvToast.toast(e.message || '更新题材勾选状态失败', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      }
    },
    loadTopicExclusionsFromLocal() {
      try {
        const raw = localStorage.getItem('panorama_topic_uncheck_expirations')
        if (!raw) return
        const map = JSON.parse(raw)
        const nowSec = Date.now() / 1000
        let changed = false
        Object.keys(map).forEach(key => {
          const exp = Number(map[key])
          if (exp > nowSec) {
            this.$set(this.topicCheckMap, key, {
              checked: false,
              expireAt: exp,
              remainingSeconds: Math.round(exp - nowSec)
            })
          } else {
            delete map[key]
            changed = true
          }
        })
        if (changed) {
          localStorage.setItem('panorama_topic_uncheck_expirations', JSON.stringify(map))
        }
      } catch (e) {
        console.warn('读取本地题材排除状态失败', e)
      }
    },
    saveTopicExclusionsToLocal() {
      try {
        const map = {}
        const nowSec = Date.now() / 1000
        Object.keys(this.topicCheckMap).forEach(key => {
          const item = this.topicCheckMap[key]
          if (item && item.checked === false && item.expireAt > nowSec) {
            map[key] = item.expireAt
          }
        })
        localStorage.setItem('panorama_topic_uncheck_expirations', JSON.stringify(map))
      } catch (e) {
        console.warn('持久化本地题材排除状态失败', e)
      }
    },
    syncTopicChecksFromBackend() {
      if (!Array.isArray(this.topics)) return
      const nowSec = Date.now() / 1000
      this.topics.forEach(t => {
        const cleanName = this.getCleanTopicName(t.topic_name)
        if (t.user_checked === false && t.user_expire_at && t.user_expire_at > nowSec) {
          this.$set(this.topicCheckMap, cleanName, {
            checked: false,
            expireAt: t.user_expire_at,
            remainingSeconds: t.user_remaining_seconds || Math.round(t.user_expire_at - nowSec)
          })
        } else if (t.user_checked === true) {
          if (this.topicCheckMap[cleanName] && this.topicCheckMap[cleanName].checked === false) {
            const exp = this.topicCheckMap[cleanName].expireAt
            if (exp && exp <= nowSec) {
              this.$set(this.topicCheckMap, cleanName, { checked: true, expireAt: 0, remainingSeconds: 0 })
            }
          }
        }
      })
      this.saveTopicExclusionsToLocal()
    }
  }
}
</script>

<style scoped>
.panorama-page {
  min-height: 100vh;
  background-color: var(--bg-main, #f8fafc);
  color: var(--text-main, #1e293b);
  --p-card-bg: var(--bg-card, #ffffff);
  --p-border: var(--border-color, #e2e8f0);
  --p-text-main: var(--text-main, #1e293b);
  --p-text-muted: var(--text-muted, #64748b);
  --p-pill-bg: rgba(0, 0, 0, 0.04);
  --p-pill-border: rgba(0, 0, 0, 0.1);
  --p-input-bg: #ffffff;
  --p-input-border: #cbd5e1;
  --p-input-color: #1e293b;
  --p-table-hover: rgba(0, 0, 0, 0.03);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.panorama-page.dark-mode {
  background-color: #0f1219;
  color: #ffffff;
  --p-card-bg: #1a1e29;
  --p-border: rgba(255, 255, 255, 0.1);
  --p-text-main: #ffffff;
  --p-text-muted: #94a3b8;
  --p-pill-bg: rgba(255, 255, 255, 0.08);
  --p-pill-border: rgba(255, 255, 255, 0.15);
  --p-input-bg: rgba(255, 255, 255, 0.06);
  --p-input-border: rgba(255, 255, 255, 0.14);
  --p-input-color: #ffffff;
  --p-table-hover: rgba(255, 255, 255, 0.05);
}

.text-title,
.topic-name-title,
.stock-name-link,
.leader-names,
.target-name,
.counts-text {
  color: var(--p-text-main) !important;
}

.panorama-card {
  background-color: var(--p-card-bg) !important;
  border: 1px solid var(--p-border) !important;
  color: var(--p-text-main);
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.panorama-divider {
  border-color: var(--p-border) !important;
}

.panorama-pill {
  background-color: var(--p-pill-bg);
  border-color: var(--p-pill-border) !important;
}

.custom-datepicker {
  color: var(--p-text-main) !important;
}
.custom-datepicker::-webkit-calendar-picker-indicator {
  cursor: pointer;
}
.dark-mode .custom-datepicker::-webkit-calendar-picker-indicator {
  filter: invert(0.8);
}

.panorama-switch {
  color: var(--p-text-main);
}

.target-summary-card {
  background-color: var(--p-pill-bg);
  border: 1px solid var(--p-border);
}

.position-config-row {
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.strategy-label {
  min-width: 52px;
  padding-bottom: 0.3rem;
  font-weight: 700;
  font-size: 0.8rem;
}
.position-input-wrap {
  width: 78px;
}
.position-input-wrap label {
  color: var(--p-text-muted);
  display: block;
  font-size: 0.65rem;
  margin-bottom: 0;
  line-height: 1.1;
}
.position-input-wrap .form-control,
.position-input-wrap .input-group-text {
  background: var(--p-input-bg);
  border-color: var(--p-input-border);
  color: var(--p-input-color);
  height: 27px;
  padding-top: 0.15rem;
  padding-bottom: 0.15rem;
  font-size: 0.75rem;
}
.position-total {
  color: var(--p-text-muted);
  font-size: 0.75rem;
  padding-bottom: 0.3rem;
  margin-left: auto;
}
.position-total strong { color: var(--p-text-main); }
.today-buy-targets strong { color: #0284c7; font-weight: 600; }
.dark-mode .today-buy-targets strong { color: #7dd3fc; }

.crowding-card {
  background: var(--p-card-bg);
}
.crowding-normal { border-left: 4px solid #28a745 !important; }
.crowding-reduced { border-left: 4px solid #ffc107 !important; }
.crowding-blocked { border-left: 4px solid #dc3545 !important; }
.crowding-unknown { border-left: 4px solid #6c757d !important; }
.crowding-ratio { color: var(--p-text-main); font-size: 1.75rem; font-weight: 700; line-height: 1; }
.crowding-threshold-note { color: var(--p-text-muted); font-size: 0.65rem; white-space: nowrap; }
.crowding-advice { color: #b45309; font-size: 0.85rem; font-weight: 600; }
.dark-mode .crowding-advice { color: #f3d58a; }
.crowding-result { min-width: 0; }
.topic-sequence { color: var(--p-text-muted); font-variant-numeric: tabular-nums; }
@media (max-width: 767.98px) {
  .position-input-wrap { width: calc(50% - 34px); }
  .position-total { width: 100%; margin-left: 34px; }
  .crowding-content { align-items: flex-start !important; flex-wrap: wrap; }
  .crowding-result { margin-top: 0.4rem; width: 100%; }
}
.market-style-panel {
  font-size: 0.72rem;
}
.market-style-summary {
  line-height: 1.35;
  transition: all 0.2s ease;
}
.style-summary-blocked {
  background-color: rgba(239, 68, 68, 0.12);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.3);
}
.style-summary-defense {
  background-color: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.25);
}
.style-summary-bull {
  background-color: rgba(16, 185, 129, 0.1);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.25);
}
.style-summary-normal {
  background-color: var(--p-pill-bg);
  color: var(--p-text-muted);
  border: 1px solid var(--p-border);
}
.weak-low-volume-rule {
  background-color: var(--p-pill-bg);
  border: 1px solid var(--p-border);
  border-radius: 0.375rem;
  padding: 0.5rem;
}
.climax-help-icon {
  color: var(--p-text-muted);
  cursor: help;
  font-size: 0.78rem;
  vertical-align: -0.08em;
}
.climax-summary-status {
  color: var(--p-text-main);
  font-weight: 600;
}
.weak-low-volume-rule.is-blocked {
  background-color: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}
.weak-low-volume-rule.is-blocked .weak-low-volume-detail {
  color: #dc2626;
}
.weak-low-volume-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}
.weak-low-volume-metric {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.weak-low-volume-metric span,
.weak-low-volume-metric small {
  color: var(--p-text-muted);
}
.weak-low-volume-metric strong {
  color: var(--p-text-main);
}
.weak-low-volume-rule.is-blocked .weak-low-volume-metric:last-child strong {
  color: #dc2626;
}
.weak-low-volume-detail {
  margin-top: 0.4rem;
  color: var(--p-text-muted);
  line-height: 1.35;
}
@media (max-width: 575.98px) {
  .weak-low-volume-metrics {
    grid-template-columns: 1fr;
  }
}
.dark-mode .style-summary-blocked {
  background-color: rgba(239, 68, 68, 0.18);
  color: #fca5a5;
  border-color: rgba(239, 68, 68, 0.4);
}
.dark-mode .style-summary-defense {
  background-color: rgba(245, 158, 11, 0.16);
  color: #fcd34d;
  border-color: rgba(245, 158, 11, 0.35);
}
.dark-mode .style-summary-bull {
  background-color: rgba(16, 185, 129, 0.16);
  color: #6ee7b7;
  border-color: rgba(16, 185, 129, 0.35);
}
.dark-mode .weak-low-volume-rule.is-blocked {
  background-color: rgba(239, 68, 68, 0.16);
  border-color: rgba(239, 68, 68, 0.38);
}
.dark-mode .weak-low-volume-rule.is-blocked .weak-low-volume-detail,
.dark-mode .weak-low-volume-rule.is-blocked .weak-low-volume-metric:last-child strong {
  color: #fca5a5;
}
.dark-mode .weak-low-volume-metric strong {
  color: var(--p-text-main);
}
.dark-mode .climax-help-icon {
  color: var(--p-text-muted);
}
.mode-btn-group .btn {
  font-size: 0.68rem;
  line-height: 1.35;
}
.rebound-candidates-box {
  background-color: var(--p-pill-bg);
  border: 1px solid var(--p-border);
}
.rebound-candidate-card {
  background-color: var(--p-card-bg);
  border: 1px solid var(--p-border);
}
.rebound-leader-box {
  background-color: var(--p-pill-bg);
  border: 1px solid var(--p-border);
}
.rebound-stock-link {
  background: transparent;
  border: 0;
  padding: 0;
  color: var(--p-text-main);
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: color 0.15s ease;
}
.rebound-stock-link:hover {
  color: #0d6efd;
  text-decoration: underline;
}
.dark-mode .rebound-stock-link:hover,
body.theme-dark .rebound-stock-link:hover {
  color: #38bdf8;
}
.rebound-gene-btn {
  display: inline-flex;
  align-items: center;
  background-color: var(--p-pill-bg, rgba(0, 0, 0, 0.04));
  color: var(--p-text-main, #1e293b);
  border: 1px solid var(--p-border, rgba(0, 0, 0, 0.12));
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.65rem;
  line-height: 1.25;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  text-decoration: none;
  outline: none;
}
.rebound-gene-btn:hover {
  background-color: rgba(13, 110, 253, 0.1);
  border-color: rgba(13, 110, 253, 0.4);
  color: #0d6efd;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}
.dark-mode .rebound-gene-btn,
body.theme-dark .rebound-gene-btn {
  background-color: rgba(255, 255, 255, 0.06);
  color: #e2e8f0;
  border-color: rgba(255, 255, 255, 0.12);
}
.dark-mode .rebound-gene-btn:hover,
body.theme-dark .rebound-gene-btn:hover {
  background-color: rgba(56, 189, 248, 0.18);
  border-color: rgba(56, 189, 248, 0.45);
  color: #38bdf8;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
.rebound-history {
  overflow-wrap: anywhere;
}
.text-muted-gold {
  color: #bfa77b !important;
}
.hover-primary:hover {
  color: var(--primary) !important;
}
.x-small {
  font-size: 0.75rem;
}
.xx-small {
  font-size: 0.65rem;
}
.compact-table td, .compact-table th {
  padding: 0.16rem 0.12rem !important;
  vertical-align: middle !important;
}
.custom-row-5 > .col-xl-20 {
  flex: 0 0 20%;
  max-width: 20%;
}

.panorama-table {
  background-color: transparent;
  color: var(--p-text-main);
}
.panorama-table th {
  color: var(--p-text-muted);
  border-bottom: 1px solid var(--p-border) !important;
}
.panorama-table td {
  border-top: 1px solid var(--p-border);
}
.panorama-table tbody tr:hover {
  background-color: var(--p-table-hover);
}

/* 徽章配色：默认高对比度浅色模式 */
.badge-met-condition {
  background-color: #f3e8ff !important;
  color: #6d28d9 !important;
  border: 1px solid #d8b4fe !important;
}
.badge-avoid {
  background-color: #dbeafe !important;
  color: #1d4ed8 !important;
  border: 1px solid #93c5fd !important;
}
.badge-net-positive {
  background-color: #fee2e2 !important;
  color: #b91c1c !important;
  border: 1px solid #fca5a5 !important;
}
.badge-net-zero {
  background-color: #f1f5f9 !important;
  color: #475569 !important;
  border: 1px solid #cbd5e1 !important;
}
.badge-net-negative {
  background-color: #dcfce7 !important;
  color: #15803d !important;
  border: 1px solid #86efac !important;
}
.badge-collapse {
  background-color: #fee2e2 !important;
  color: #b91c1c !important;
  border: 1px solid #f87171 !important;
}
.badge-buy {
  background-color: #fee2e2 !important;
  color: #b91c1c !important;
  border: 1px solid #f87171 !important;
}
.badge-v3-silent {
  background-color: #fef3c7 !important;
  color: #b45309 !important;
  border: 1px solid #fcd34d !important;
}
.badge-placeholder {
  background-color: #fef9c3 !important;
  color: #a16207 !important;
  border: 1px solid #fde047 !important;
}
.badge-muted-tag {
  background-color: #f1f5f9 !important;
  color: #64748b !important;
  border: 1px solid #e2e8f0 !important;
}
.badge-untriggered {
  background-color: #f8fafc !important;
  color: #94a3b8 !important;
  border: 1px solid #e2e8f0 !important;
}

/* 深色模式下的徽章霓虹暗底配色 */
.dark-mode .badge-met-condition {
  background-color: rgba(139, 92, 246, 0.18) !important;
  color: #c084fc !important;
  border: 1px solid rgba(139, 92, 246, 0.4) !important;
}
.dark-mode .badge-avoid {
  background-color: rgba(59, 130, 246, 0.16) !important;
  color: #93c5fd !important;
  border: 1px solid rgba(59, 130, 246, 0.35) !important;
}
.dark-mode .badge-net-positive {
  background-color: rgba(239, 68, 68, 0.2) !important;
  color: #f87171 !important;
  border: 1px solid rgba(239, 68, 68, 0.4) !important;
}
.dark-mode .badge-net-zero {
  background-color: rgba(148, 163, 184, 0.15) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.3) !important;
}
.dark-mode .badge-net-negative {
  background-color: rgba(34, 197, 94, 0.2) !important;
  color: #4ade80 !important;
  border: 1px solid rgba(34, 197, 94, 0.4) !important;
}
.dark-mode .badge-collapse {
  background-color: rgba(239, 68, 68, 0.22) !important;
  color: #fca5a5 !important;
  border: 1px solid rgba(239, 68, 68, 0.5) !important;
}
.dark-mode .badge-buy {
  background-color: rgba(220, 53, 69, 0.24) !important;
  color: #ff6b6b !important;
  border: 1px solid rgba(220, 53, 69, 0.55) !important;
}
.dark-mode .badge-v3-silent {
  background-color: rgba(245, 158, 11, 0.2) !important;
  color: #fcd34d !important;
  border: 1px solid rgba(245, 158, 11, 0.45) !important;
}
.dark-mode .badge-placeholder {
  background-color: rgba(234, 179, 8, 0.2) !important;
  color: #fde047 !important;
  border: 1px solid rgba(234, 179, 8, 0.45) !important;
}
.dark-mode .badge-muted-tag {
  background-color: rgba(148, 163, 184, 0.1) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.22) !important;
}
.dark-mode .badge-untriggered {
  background-color: rgba(255, 255, 255, 0.04) !important;
  color: #64748b !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.micro-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.6rem !important;
  line-height: 1.15;
  padding: 1px 3px !important;
  border-radius: 2px;
  letter-spacing: -0.2px;
  white-space: nowrap;
}
.micro-prefix {
  font-weight: 700;
  margin-right: 1px;
  font-size: 0.58rem;
}
.v2-p {
  color: #d97706;
}
.dark-mode .v2-p {
  color: #ffc107;
}
.v3-p {
  color: #0284c7;
}
.dark-mode .v3-p {
  color: #0dcaf0;
}
.status-badge-compact {
  font-size: 0.61rem !important;
  padding: 1px 4px !important;
  line-height: 1.15;
  border-radius: 2px;
}

.fire-btn {
  font-size: 0.68rem !important;
  line-height: 1 !important;
  cursor: pointer;
  filter: drop-shadow(0 0 2px rgba(255, 193, 7, 0.6));
  transition: transform 0.15s ease;
  vertical-align: middle;
  text-decoration: none !important;
}
.fire-btn:hover {
  transform: scale(1.25);
}

.score-pill {
  display: inline-block;
  font-family: var(--font-monospace, monospace);
  font-size: 0.58rem;
  font-weight: 700;
  line-height: 1;
  padding: 1px 3px;
  border-radius: 2px;
  cursor: help;
  white-space: nowrap;
}
.score-high {
  color: #047857;
  background-color: #d1fae5;
  border: 1px solid #6ee7b7;
}
.score-mid {
  color: #0284c7;
  background-color: #e0f2fe;
  border: 1px solid #7dd3fc;
}
.score-low {
  color: #475569;
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
}

.dark-mode .score-high {
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.16);
  border: 1px solid rgba(16, 185, 129, 0.38);
}
.dark-mode .score-mid {
  color: #38bdf8;
  background-color: rgba(56, 189, 248, 0.14);
  border: 1px solid rgba(56, 189, 248, 0.32);
}
.dark-mode .score-low {
  color: #94a3b8;
  background-color: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.22);
}

.border-success-50 {
  border-color: rgba(40, 167, 69, 0.55) !important;
  box-shadow: 0 0 6px rgba(40, 167, 69, 0.2);
}
.pulse-dot {
  display: inline-block;
  color: #28a745;
  animation: pulse-dot-anim 2s infinite ease-in-out;
}
@keyframes pulse-dot-anim {
  0% { transform: scale(0.9); opacity: 0.5; }
  50% { transform: scale(1.25); opacity: 1; text-shadow: 0 0 4px #28a745; }
  100% { transform: scale(0.9); opacity: 0.5; }
}
.today-buy-target-item {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.fire-buy-btn {
  font-size: 0.85rem;
  line-height: 1;
  vertical-align: middle;
  text-decoration: none !important;
  margin-left: 2px;
  cursor: pointer;
  transition: transform 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.fire-buy-btn:hover {
  transform: scale(1.35);
  filter: drop-shadow(0 0 5px rgba(255, 68, 0, 0.9));
}
.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.12) !important;
  color: #ffc107 !important;
}
.strategy-select-group .btn {
  border-radius: 6px;
  transition: all 0.2s ease;
}

.candidate-tabs ::v-deep .nav-tabs {
  border-color: var(--p-border);
  margin-bottom: 0.75rem;
}
.candidate-tabs ::v-deep .nav-link {
  color: var(--p-text-muted);
  border-radius: 0.5rem 0.5rem 0 0;
}
.candidate-tabs ::v-deep .nav-link.active {
  color: var(--p-text-main);
  background: var(--p-card-bg);
  border-color: var(--p-border);
}
.candidate-tabs ::v-deep .nav-link:hover {
  border-color: var(--p-border);
}


.risk-compact-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
}
.risk-compact-ratio { font-size: 1.5rem; line-height: 1.2; }
.risk-compact-advice { font-size: 0.8rem; font-weight: 600; margin: 4px 0; color: var(--p-text-main); }
.risk-compact-style { border-top: 1px solid var(--p-border); padding-top: 6px; margin-top: 6px; }
.risk-compact-indicators { display: flex; flex-wrap: wrap; gap: 4px 14px; margin: 6px 0; color: var(--p-text-muted); }
.risk-compact-indicators strong { color: var(--p-text-main); }
.risk-compact-gate { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px; padding: 6px 8px; border-radius: 6px; background: var(--p-pill-bg); color: var(--p-text-main); }
.risk-compact-gate.is-blocked { border: 1px solid var(--danger); }
.risk-compact-details { margin-top: 6px; }
.risk-compact-details summary { cursor: pointer; color: var(--p-text-muted); padding: 2px 0; }
.risk-compact-details summary:focus-visible { outline: 2px solid var(--p-text-main); outline-offset: 2px; }
.rebound-stock-table { width: 100%; }
.rebound-stock-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.rebound-stock-group { min-width: 0; border: 1px solid var(--p-border); border-radius: 6px; padding: 4px; background: var(--p-card-bg); }
@media (max-width: 1399px) { .rebound-stock-grid { grid-template-columns: minmax(0, 1fr); } }
.rebound-stock-link {
  background: transparent;
  border: 0;
  padding: 0;
  color: var(--p-text-main);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1.2;
}
.rebound-stock-link:hover {
  color: var(--primary) !important;
  text-decoration: underline;
}
.rebound-stock-code {
  font-size: 0.62rem;
  letter-spacing: -0.2px;
  white-space: nowrap;
  opacity: 0.85;
  line-height: 1.2;
}
.rebound-topic-cell { white-space: normal; min-width: 100px; max-width: 200px; }
.rebound-topic-tag {
  display: inline-block;
  margin: 1px 3px 1px 0;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.62rem;
  background: var(--p-pill-bg);
  border: 1px solid var(--p-border);
  color: var(--p-text-muted);
  white-space: nowrap;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.rebound-gene-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 0.62rem;
  background: var(--p-pill-bg);
  border: 1px solid var(--p-border);
  color: var(--p-text-main);
  white-space: nowrap;
}

.rebound-list-header { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 4px 12px; padding: 2px 4px 5px; }
.rebound-stock-table th { background: var(--p-card-bg); }
.rebound-stock-table th, .rebound-stock-table td { padding: 4px 5px; vertical-align: middle; }
.rebound-topic-details { margin-top: 8px; border: 1px solid var(--p-border); border-radius: 6px; padding: 4px 8px; background: var(--p-card-bg); }
.rebound-topic-details summary { cursor: pointer; color: var(--p-text-muted); padding: 3px 0; font-weight: 500; }
.rebound-topic-details summary:focus-visible { outline: 2px solid var(--p-text-main); }

/* 表头排序样式 */
.sortable-th {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s ease, background-color 0.15s ease;
  white-space: nowrap;
}
.sortable-th:hover {
  color: var(--text-warning, #ffc107) !important;
}
.sortable-th .sort-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
  vertical-align: middle;
}
.sortable-th .sort-icon-active {
  color: #ffc107;
  font-size: 0.72rem;
  filter: drop-shadow(0 0 2px rgba(255, 193, 7, 0.4));
}
.sortable-th .sort-icon-idle {
  font-size: 0.58rem;
  opacity: 0.25;
  transition: opacity 0.15s ease;
}
.sortable-th:hover .sort-icon-idle {
  opacity: 0.8;
  color: #ffc107;
}
.sort-active-badge {
  font-size: 0.6rem;
  padding: 1px 6px;
  line-height: 1.3;
  font-weight: 500;
  border-radius: 4px;
  transition: opacity 0.15s ease;
}
.sort-active-badge:hover {
  opacity: 0.85;
}
</style>

<style>
/* 全局深色模式穿透适配 */
body.theme-dark .panorama-page {
  background-color: #0f1219;
  color: #ffffff;
  --p-card-bg: #1a1e29;
  --p-border: rgba(255, 255, 255, 0.1);
  --p-text-main: #ffffff;
  --p-text-muted: #94a3b8;
  --p-pill-bg: rgba(255, 255, 255, 0.08);
  --p-pill-border: rgba(255, 255, 255, 0.15);
  --p-input-bg: rgba(255, 255, 255, 0.06);
  --p-input-border: rgba(255, 255, 255, 0.14);
  --p-input-color: #ffffff;
  --p-table-hover: rgba(255, 255, 255, 0.05);
}
body.theme-dark .custom-datepicker::-webkit-calendar-picker-indicator {
  filter: invert(0.8);
}
body.theme-dark .badge-collapse {
  background-color: rgba(239, 68, 68, 0.22) !important;
  color: #fca5a5 !important;
  border: 1px solid rgba(239, 68, 68, 0.5) !important;
}
body.theme-dark .badge-net-positive {
  background-color: rgba(239, 68, 68, 0.2) !important;
  color: #f87171 !important;
  border: 1px solid rgba(239, 68, 68, 0.4) !important;
}
body.theme-dark .badge-net-zero {
  background-color: rgba(148, 163, 184, 0.15) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.3) !important;
}
body.theme-dark .badge-net-negative {
  background-color: rgba(34, 197, 94, 0.2) !important;
  color: #4ade80 !important;
  border: 1px solid rgba(34, 197, 94, 0.4) !important;
}
body.theme-dark .badge-avoid {
  background-color: rgba(59, 130, 246, 0.16) !important;
  color: #93c5fd !important;
  border: 1px solid rgba(59, 130, 246, 0.35) !important;
}
body.theme-dark .badge-met-condition {
  background-color: rgba(139, 92, 246, 0.18) !important;
  color: #c084fc !important;
  border: 1px solid rgba(139, 92, 246, 0.4) !important;
}
body.theme-dark .badge-buy {
  background-color: rgba(220, 53, 69, 0.24) !important;
  color: #ff6b6b !important;
  border: 1px solid rgba(220, 53, 69, 0.55) !important;
}
body.theme-dark .badge-v3-silent {
  background-color: rgba(245, 158, 11, 0.2) !important;
  color: #fcd34d !important;
  border: 1px solid rgba(245, 158, 11, 0.45) !important;
}
body.theme-dark .badge-placeholder {
  background-color: rgba(234, 179, 8, 0.2) !important;
  color: #fde047 !important;
  border: 1px solid rgba(234, 179, 8, 0.45) !important;
}
body.theme-dark .badge-muted-tag {
  background-color: rgba(148, 163, 184, 0.1) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.22) !important;
}
body.theme-dark .badge-untriggered {
  background-color: rgba(255, 255, 255, 0.04) !important;
  color: #64748b !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
body.theme-dark .score-high {
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.16);
  border: 1px solid rgba(16, 185, 129, 0.38);
}
body.theme-dark .score-mid {
  color: #38bdf8;
  background-color: rgba(56, 189, 248, 0.14);
  border: 1px solid rgba(56, 189, 248, 0.32);
}
body.theme-dark .score-low {
  color: #94a3b8;
  background-color: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.22);
}
body.theme-dark .today-buy-targets strong {
  color: #7dd3fc;
}
body.theme-dark .crowding-advice {
  color: #f3d58a;
}
body.theme-dark .v2-p {
  color: #ffc107;
}
body.theme-dark .v3-p {
  color: #0dcaf0;
}
body.theme-dark .style-summary-blocked {
  background-color: rgba(239, 68, 68, 0.18);
  color: #fca5a5;
  border-color: rgba(239, 68, 68, 0.4);
}
body.theme-dark .style-summary-defense {
  background-color: rgba(245, 158, 11, 0.16);
  color: #fcd34d;
  border-color: rgba(245, 158, 11, 0.35);
}
body.theme-dark .style-summary-bull {
  background-color: rgba(16, 185, 129, 0.16);
  color: #6ee7b7;
  border-color: rgba(16, 185, 129, 0.35);
}

/* 未勾选备选题材卡片样式 */
.card-unchecked-excluded {
  opacity: 0.58;
  filter: grayscale(25%);
  border-style: dashed !important;
  border-color: rgba(150, 150, 150, 0.45) !important;
  transition: opacity 0.25s ease, filter 0.25s ease, border-color 0.25s ease;
}
.card-unchecked-excluded:hover {
  opacity: 0.92;
  filter: grayscale(0%);
  border-color: rgba(180, 180, 180, 0.75) !important;
}
.badge-excluded-12h {
  background-color: rgba(148, 163, 184, 0.25) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.4);
}
.dark-mode .badge-excluded-12h {
  background-color: rgba(100, 116, 139, 0.3) !important;
  color: #cbd5e1 !important;
  border: 1px solid rgba(148, 163, 184, 0.35);
}
.custom-switch-topic.custom-control.custom-switch {
  padding-left: 1.85rem;
  margin-bottom: 0;
  min-height: 1.25rem;
  line-height: 1.25rem;
  vertical-align: middle;
}
.custom-switch-topic /deep/ .custom-control-label {
  cursor: pointer;
  user-select: none;
}
.custom-switch-topic /deep/ .custom-control-label::before {
  height: 1.05rem;
  width: 1.8rem;
  border-radius: 0.55rem;
  background-color: rgba(148, 163, 184, 0.4);
  border-color: rgba(148, 163, 184, 0.5);
  cursor: pointer;
  top: 0.1rem;
}
.dark-mode .custom-switch-topic /deep/ .custom-control-label::before {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}
.custom-switch-topic /deep/ .custom-control-label::after {
  width: calc(1.05rem - 4px);
  height: calc(1.05rem - 4px);
  border-radius: 50%;
  background-color: #ffffff;
  cursor: pointer;
  top: calc(0.1rem + 2px);
}
.custom-switch-topic /deep/ .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #10b981;
  border-color: #059669;
}
.custom-switch-topic /deep/ .custom-control-input:checked ~ .custom-control-label::after {
  transform: translateX(0.75rem);
  background-color: #ffffff;
}
.badge-topic-included {
  background-color: rgba(16, 185, 129, 0.15) !important;
  color: #10b981 !important;
  border: 1px solid rgba(16, 185, 129, 0.3);
  font-size: 0.65rem;
  padding: 0.1rem 0.35rem;
}
.dark-mode .badge-topic-included {
  background-color: rgba(16, 185, 129, 0.22) !important;
  color: #6ee7b7 !important;
  border: 1px solid rgba(16, 185, 129, 0.4);
}
.badge-topic-excluded {
  background-color: rgba(239, 68, 68, 0.18) !important;
  color: #ef4444 !important;
  border: 1px solid rgba(239, 68, 68, 0.35);
  font-size: 0.65rem;
  padding: 0.1rem 0.35rem;
}
.dark-mode .badge-topic-excluded {
  background-color: rgba(239, 68, 68, 0.25) !important;
  color: #fca5a5 !important;
  border: 1px solid rgba(239, 68, 68, 0.45);
}

</style>
